import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  FormControlLabel,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import {
  ChevronLeft as PreviousIcon,
  ChevronRight as NextIcon,
  Today as TodayIcon,
  CalendarViewWeek as WeekViewIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Timeline as TimelineIcon,
  Warning as WarningIcon,
  List as ListIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { fetchDrivers } from '../../redux/slices/driversSlice';
import { fetchVehicles, startVehiclesStatusListener } from '../../redux/slices/vehiclesSlice';
// fetchRides nicht mehr nötig — onSnapshot-Listener liefern die Daten
import { fetchPlannedShifts } from '../../redux/slices/plannedShiftsSlice';
import PageHeader from '../common/PageHeader';
import { useRideDialog } from '../rides/RideDialogProvider';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import { onSnapshot, collection, query, where, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { formatVehicleWithCapabilities, getCompatibleVehicleTypes, migrateSpecialVehicleType } from '../../config/vehicleTypes';
import { useWaitTimes } from '../../hooks/useWaitTimeManager';
import { getActualRideDurationMinutes } from '../../utils/rideDuration';
import { getRideStatusLabel } from '../../utils/rideStatusLabels';
import { isOperatorRole } from '../../redux/slices/authSlice';
import { useTransportWording } from '../../utils/transportWording';

// Dayjs auf Deutsch einstellen
dayjs.locale('de');

// Hilfsfunktion für aktive Schicht im neuen Array-System
const getActiveShift = (driverData) => {
  if (!driverData || !driverData.shifts || !Array.isArray(driverData.shifts)) {
    return null;
  }
  return driverData.shifts.find(shift => shift.isActive) || null;
};

const getDriverName = (driver) => (
  driver?.user?.name ||
  driver?.name ||
  driver?.driverName ||
  'Unbekannter Fahrer'
);

const getVehicleLabel = (vehicle) => {
  if (!vehicle) return 'Kein Fahrzeug';
  const model = vehicle.model || vehicle.name || 'Fahrzeug';
  return model;
};

const normalizeSortableNumber = (value) => {
  if (value === undefined || value === null || value === '') return Number.POSITIVE_INFINITY;
  const parsed = Number(String(value).replace(/[^\d]/g, ''));
  return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
};

const compareLabels = (a, b) => String(a || '').localeCompare(String(b || ''), 'de', { sensitivity: 'base' });

const VehicleNumberBadge = ({ value, compact = false }) => (
  <Typography
    variant="caption"
    component="span"
    sx={{
      minWidth: compact ? 24 : 32,
      px: compact ? 0.4 : 0.5,
      py: compact ? 0 : 0.1,
      borderRadius: 0.75,
      bgcolor: value ? 'primary.main' : 'grey.300',
      color: value ? 'primary.contrastText' : 'text.secondary',
      fontWeight: 700,
      textAlign: 'center',
      lineHeight: compact ? 1.35 : 1.45,
      fontSize: compact ? '0.65rem' : undefined,
      flexShrink: 0
    }}
  >
    {value || '-'}
  </Typography>
);

const getActivityVisualState = ({ theme, type, orderType, isDelayed, isPending, reason }) => {
  const alpha = (color, opacity) => `${color}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`;
  const palette = theme.palette;

  if (type === 'ride') {
    const base = orderType === 1
      ? palette.info
      : orderType === 2
        ? palette.success
        : isPending
          ? palette.warning
          : palette.primary;

    return {
      accent: isDelayed ? palette.error.main : base.main,
      background: isDelayed ? alpha(palette.error.main, 0.1) : alpha(base.main, 0.12),
      border: isDelayed ? palette.error.main : alpha(base.main, 0.45),
      color: base.dark || base.main,
      label: orderType === 1 ? 'Vorbestellung' : 'Fahrt'
    };
  }

  if (type === 'break' || type === 'pause') {
    return {
      accent: palette.warning.main,
      background: alpha(palette.warning.main, 0.14),
      border: alpha(palette.warning.main, 0.5),
      color: palette.warning.dark,
      label: 'Pause'
    };
  }

  if (type === 'offline') {
    const reasonAccent = reason === 'connection_lost'
      ? palette.grey[700]
      : reason === 'app_closed'
        ? palette.warning.main
        : palette.error.main;

    return {
      accent: reasonAccent,
      background: alpha(reasonAccent, 0.1),
      border: alpha(reasonAccent, 0.38),
      color: palette.text.primary,
      label: 'Offline'
    };
  }

  if (type === 'reconnect') {
    return {
      accent: palette.success.main,
      background: alpha(palette.success.main, 0.1),
      border: alpha(palette.success.main, 0.38),
      color: palette.success.dark,
      label: 'Online'
    };
  }

  if (type === 'planned_shift') {
    return {
      accent: palette.grey[500],
      background: palette.action.selected,
      border: palette.divider,
      color: palette.text.secondary,
      label: 'Schicht'
    };
  }

  return {
    accent: palette.primary.main,
    background: alpha(palette.primary.main, 0.12),
    border: alpha(palette.primary.main, 0.45),
    color: palette.primary.dark,
    label: 'Aktivität'
  };
};

const getActivityBarLabel = (activity, endTime) => {
  if (activity.type === 'offline') {
    return `Offline · bis ${endTime}`;
  }
  if (activity.type === 'break' || activity.type === 'pause') {
    return `${activity.destination || 'Pause'} · bis ${endTime}`;
  }
  if (activity.type === 'planned_shift') {
    return `Schicht · bis ${endTime}`;
  }
  if (activity.type === 'ride') {
    const prefix = activity.orderType === 1 ? 'Vorbestellung' : 'Fahrt';
    const destination = activity.destination || 'Ziel offen';
    return `${prefix} · ${destination}`;
  }
  return activity.destination || 'Aktivität';
};

// Kalender-eigene Zustände; die Fahrt-Status kommen aus rideStatusLabels.
const ACTIVITY_STATUS_LABELS = {
  active: 'Aktiv',
  planned: 'Geplant'
};

const getRideTypeLabel = (orderType) => {
  if (orderType === 1) return 'Vorbestellung';
  if (orderType === 2) return 'Sofortfahrt';
  return 'Fahrt';
};

// Die Aktivität trägt orderType, deshalb greift auch hier das fahrtbezogene
// Label ("Zugesagt" / "Angetreten" bei Vorbestellungen).
const getActivityStatusLabel = (activity) => (
  ACTIVITY_STATUS_LABELS[activity?.status] ||
  getRideStatusLabel(activity, activity?.status)
);

const formatMinutesLabel = (minutes) => {
  const parsed = Number(minutes);
  if (!Number.isFinite(parsed)) return null;

  const totalMinutes = Math.max(0, Math.round(parsed));
  if (totalMinutes < 60) return `${totalMinutes} Min`;

  const hours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}min` : `${hours}h`;
};

const formatDurationBetween = (startTime, endTime) => {
  if (!startTime || !endTime) return null;
  return formatMinutesLabel((endTime.getTime() - startTime.getTime()) / 60000);
};

const getVehicleTooltipLabel = (vehicle, fallbackNumber) => {
  if (!vehicle) return 'Fahrzeug noch nicht sicher';

  const vehicleNumber = vehicle.vehicleNumber || fallbackNumber;
  const model = vehicle.model || vehicle.name || 'Fahrzeug';
  const licensePlate = vehicle.licensePlate || vehicle.license_plate || vehicle.plate || vehicle.registrationNumber;

  return [
    vehicleNumber ? `#${vehicleNumber}` : null,
    model,
    licensePlate
  ].filter(Boolean).join(' · ');
};

const formatAddressParts = (value) => {
  if (!value || typeof value !== 'object') return '';

  const street = [value.street, value.streetName, value.road].filter(Boolean).join(' ');
  const houseNumber = value.houseNumber || value.streetNumber || value.number;
  const cityLine = [value.postalCode || value.zip, value.city || value.locality].filter(Boolean).join(' ');
  const parts = [
    [street, houseNumber].filter(Boolean).join(' '),
    cityLine,
    value.country
  ].filter(Boolean);

  return parts.join(', ');
};

const extractAddressValue = (...values) => {
  for (const value of values) {
    if (!value) continue;

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
      continue;
    }

    if (typeof value !== 'object') continue;

    const directValue = extractAddressValue(
      value.address,
      value.formatted_address,
      value.formattedAddress,
      value.fullAddress,
      value.displayName,
      value.description,
      value.name,
      value.label,
      value.text,
      value.title
    );

    if (directValue) return directValue;

    const formattedParts = formatAddressParts(value);
    if (formattedParts) return formattedParts;
  }

  return '';
};

const getRideStartAddress = (rideData) => extractAddressValue(
  rideData.start,
  rideData.pickupAddress,
  rideData.pickup_address,
  rideData.pickup_address_text,
  rideData.startAddress,
  rideData.start_address,
  rideData.pickup,
  rideData.origin,
  rideData.from,
  rideData.pickupLocationName,
  rideData.pickupLocation,
  rideData.pickup_location,
  rideData.route?.pickup,
  rideData.route?.origin,
  rideData.addresses?.pickup,
  rideData.medicalTransportSnapshot?.pickupAddress
);

const getRideDestinationAddress = (rideData) => extractAddressValue(
  rideData.destination,
  rideData.destinationAddress,
  rideData.destination_address,
  rideData.destination_address_text,
  rideData.destinationLocationName,
  rideData.destinationLocation,
  rideData.destination_location,
  rideData.dropoff,
  rideData.dropoffAddress,
  rideData.dropoff_address,
  rideData.to,
  rideData.route?.destination,
  rideData.route?.dropoff,
  rideData.addresses?.destination,
  rideData.medicalTransportSnapshot?.destinationAddress
);

const getRideSpecialCapabilities = (rideData) => {
  if (Array.isArray(rideData.specialCapabilities)) return rideData.specialCapabilities;
  if (Array.isArray(rideData.special_capabilities)) return rideData.special_capabilities;
  return migrateSpecialVehicleType(rideData);
};

const getRideVehicleRequirementLabel = (activity) => {
  const capabilities = Array.isArray(activity.specialCapabilities) ? activity.specialCapabilities : [];
  if (!activity.vehicleType && capabilities.length === 0) return null;
  return formatVehicleWithCapabilities(activity.vehicleType || 1, capabilities);
};

const TooltipTitle = ({ children, color = 'text.primary' }) => (
  <Typography
    variant="body2"
    fontWeight={700}
    sx={{
      mb: 0.5,
      color: color === 'text.primary' ? 'common.white' : color,
      textAlign: 'left'
    }}
  >
    {children}
  </Typography>
);

const TooltipLine = ({ label, value, children, color = 'inherit' }) => {
  const content = children ?? value;
  if (content === undefined || content === null || content === '') return null;

  return (
    <Box
      component="div"
      sx={{
        display: 'grid',
        gridTemplateColumns: '96px minmax(0, 1fr)',
        columnGap: 0.75,
        alignItems: 'start',
        color: color === 'inherit' ? 'common.white' : color,
        textAlign: 'left'
      }}
    >
      <Typography
        variant="caption"
        component="span"
        sx={{
          fontWeight: 700,
          color: 'rgba(255, 255, 255, 0.72)',
          lineHeight: 1.5,
          textAlign: 'left'
        }}
      >
        {label}:
      </Typography>
      <Typography
        variant="caption"
        component="span"
        sx={{
          color: 'common.white',
          lineHeight: 1.5,
          minWidth: 0,
          overflowWrap: 'anywhere',
          textAlign: 'left'
        }}
      >
        {content}
      </Typography>
    </Box>
  );
};

const TooltipNote = ({ children, color = 'text.secondary' }) => (
  <Typography
    variant="caption"
    component="div"
    sx={{
      mt: 0.75,
      fontStyle: 'italic',
      color: color === 'text.secondary' ? 'rgba(255, 255, 255, 0.72)' : color,
      textAlign: 'left'
    }}
  >
    {children}
  </Typography>
);

const richTooltipComponentsProps = {
  tooltip: {
    sx: {
      maxWidth: 360,
      bgcolor: 'grey.900',
      color: 'common.white',
      textAlign: 'left !important',
      '& *': {
        textAlign: 'left !important'
      }
    }
  }
};

// Styled Components für das Gantt-Diagramm
const GanttContainer = styled(Paper)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  overflow: 'hidden',
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[2]
}));

const GanttHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.default
}));

const ViewToggleGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1)
}));

const TimelineContainer = styled(Box)(({ theme }) => ({
  height: '600px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  position: 'relative'
}));

const VehicleRow = styled(Box)(({ theme, isActive }) => ({
  height: '60px',
  padding: theme.spacing(1, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  backgroundColor: isActive ? theme.palette.action.selected : 'inherit',
  '&:hover': {
    backgroundColor: theme.palette.action.hover
  }
}));

const TimeHeader = styled(Box)(({ theme }) => ({
  height: '50px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  backgroundColor: theme.palette.background.default,
  position: 'relative',
  zIndex: 1
}));

const TimeSlot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isCurrentTime' && prop !== 'slotWidth',
})(({ theme, isCurrentTime, slotWidth }) => ({
  minWidth: `${slotWidth}px`,
  borderRight: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: isCurrentTime ? theme.palette.primary.light : 'inherit',
  position: 'relative'
}));

const VehicleTimelineRow = styled(Box)(({ theme }) => ({
  height: '60px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  position: 'relative'
}));

const CurrentTimeLine = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: '2px',
  backgroundColor: theme.palette.error.main,
  zIndex: 10,
  opacity: 0.8
}));

const ActivityBar = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isDelayed' && prop !== 'isPending' && prop !== 'type' && prop !== 'orderType' && prop !== 'reason',
})(({ theme, type, isDelayed, isPending, orderType, reason }) => {
  const visual = getActivityVisualState({ theme, type, orderType, isDelayed, isPending, reason });

  return {
    position: 'absolute',
    height: '28px',
    top: '16px',
    backgroundColor: visual.background,
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0, 1, 0, 1.25),
    color: visual.color,
    fontSize: '0.75rem',
    fontWeight: 600,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    border: `1px solid ${visual.border}`,
    borderLeft: `4px solid ${visual.accent}`,
    boxShadow: 'none',
    transition: theme.transitions.create(['box-shadow', 'transform', 'background-color'], {
      duration: theme.transitions.duration.shortest
    }),
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: theme.shadows[2],
      backgroundColor: theme.palette.background.paper
    }
  };
});

const ApproachTimeBar = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'orderType' && prop !== 'isDelayed',
})(({ theme, orderType, isDelayed }) => {
  const visual = getActivityVisualState({ theme, type: 'ride', orderType, isDelayed });

  return {
    position: 'absolute',
    height: '28px',
    top: '16px',
    backgroundColor: theme.palette.background.paper,
    backgroundImage: `repeating-linear-gradient(
      45deg,
      ${visual.accent}22,
      ${visual.accent}22 4px,
      transparent 4px,
      transparent 8px
    )`,
    border: `1px dashed ${visual.accent}`,
    borderRadius: '4px 0 0 4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    color: visual.accent,
    fontSize: '0.7rem',
    fontWeight: 700,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: theme.shadows[1]
    }
  };
});

const ShiftStartMarker = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '3px',
  height: '100%',
  backgroundColor: theme.palette.info.main,
  top: 0,
  zIndex: 5,
  cursor: 'pointer',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-2px',
    left: '-2px',
    width: '7px',
    height: '7px',
    backgroundColor: theme.palette.info.main,
    borderRadius: '50%'
  },
  '&:hover': {
    backgroundColor: theme.palette.info.dark,
    '&::before': {
      backgroundColor: theme.palette.info.dark
    }
  }
}));

const ShiftEndMarker = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '3px',
  height: '100%',
  backgroundColor: theme.palette.error.main,
  top: 0,
  zIndex: 5,
  cursor: 'pointer',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-2px',
    left: '-2px',
    width: '7px',
    height: '7px',
    backgroundColor: theme.palette.error.main,
    borderRadius: '50%'
  },
  '&:hover': {
    backgroundColor: theme.palette.error.dark,
    '&::before': {
      backgroundColor: theme.palette.error.dark
    }
  }
}));

const FleetUtilizationRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.default,
  minHeight: '80px'
}));

const UtilizationLabel = styled(Box)(({ theme }) => ({
  width: '250px',
  padding: theme.spacing(2),
  borderRight: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center'
}));

const UtilizationChart = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1, 0),
  position: 'relative',
  height: '20px',
  overflow: 'hidden'
}));

const UtilizationTimeline = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: 0,
  right: 0,
  height: '20px',
  display: 'flex',
  backgroundColor: theme.palette.grey[200]
}));

const UtilizationSegment = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'status',
})(({ theme, status }) => {
  const getBackgroundColor = () => {
    switch (status) {
      case 'normal':
        return theme.palette.success.main; // Grün - normal verfügbar
      case 'overtime':
        return theme.palette.warning.main; // Gelb - nur Überstunden verfügbar
      case 'planned':
        return '#ffd700'; // Gelb - geplante Schichten
      case 'unavailable':
      default:
        return theme.palette.error.main; // Rot - nicht verfügbar
    }
  };

  return {
    position: 'absolute',
    height: '100%',
    backgroundColor: getBackgroundColor(),
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.8,
      boxShadow: theme.shadows[2]
    }
  };
});

const ScheduledRideBar = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'color',
})(({ theme, color }) => ({
  position: 'absolute',
  height: '100%',
  backgroundColor: color || theme.palette.grey[900],
  opacity: 0.7,
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  border: `1px solid ${theme.palette.grey[700]}`,
  '&:hover': {
    opacity: 0.9,
    boxShadow: theme.shadows[2]
  }
}));

const WeekOverviewContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2)
}));

const WeekGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(8, 1fr)',
  gap: theme.spacing(1),
  marginTop: theme.spacing(2)
}));

const WeekCell = styled(Box)(({ theme, isHeader, utilization = 0 }) => {
  const getBackgroundColor = () => {
    if (isHeader) return theme.palette.background.default;
    
    if (utilization < 0.3) return theme.palette.success.light;
    if (utilization < 0.7) return theme.palette.warning.light;
    return theme.palette.error.light;
  };

  return {
    padding: theme.spacing(1),
    textAlign: 'center',
    backgroundColor: getBackgroundColor(),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    cursor: isHeader ? 'default' : 'pointer',
    fontWeight: isHeader ? 'bold' : 'normal',
    '&:hover': !isHeader ? {
      backgroundColor: theme.palette.action.hover
    } : {}
  };
});

const ShiftCalendar = () => {
  const wording = useTransportWording();
  const dispatch = useDispatch();
  const { openRideDialog } = useRideDialog() || {};
  
  // Redux state
  const { vehicles, isLoading: vehiclesLoading, error: vehiclesError } = useSelector(state => state.vehicles);
  const { drivers } = useSelector(state => state.drivers);
  const { plannedShifts } = useSelector(state => state.plannedShifts);
  const currentUser = useSelector(state => state.auth.user);
  
  // Global WaitTime Hook (nur für Anzeige, keine Updates mehr)
  const { waitTimes: globalWaitTimes, nextUnavailable: globalNextUnavailable } = useWaitTimes();
  
  // Local state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState('gantt'); // 'gantt', 'week'
  const [showFullDay, setShowFullDay] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [zoomLevel, setZoomLevel] = useState('12h'); // '3h', '6h', '12h'
  const [activities, setActivities] = useState([]);
  const [immediateRideActivities, setImmediateRideActivities] = useState([]);
  const [scheduledRideActivities, setScheduledRideActivities] = useState([]);
  const [breakActivities, setBreakActivities] = useState([]);
  const [offlineActivities, setOfflineActivities] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [rawShiftsData, setRawShiftsData] = useState(null);
  // Mandanten-Scoping: Ein Sub-Unternehmen sieht im Taxi-Kalender nur die
  // Schichten der EIGENEN Fahrer. `drivers` aus dem Redux-Store ist für
  // Operator-Rollen bereits auf den eigenen Operator begrenzt; zentrale Rollen
  // sehen alle. (Interim-Fix client-seitig; die Rules-Ebene R5/R6 folgt als
  // eigenes Konzept — subunternehmer_portalzugang_quittung_plan.md §6.)
  const shiftsData = useMemo(() => {
    if (!rawShiftsData || !isOperatorRole(currentUser)) return rawShiftsData;
    const allowed = new Set((drivers || []).map(d => d.id));
    const filteredDrivers = {};
    Object.entries(rawShiftsData.drivers || {}).forEach(([driverId, driverData]) => {
      if (allowed.has(driverId)) filteredDrivers[driverId] = driverData;
    });
    return { ...rawShiftsData, drivers: filteredDrivers };
  }, [rawShiftsData, currentUser, drivers]);
  const [scheduledRides, setScheduledRides] = useState([]);
  const [plannedShiftActivities, setPlannedShiftActivities] = useState([]);
  
  // Ref to hold current vehicles for use in listeners
  const vehiclesRef = useRef(vehicles);
  const timelineHeaderRef = useRef(null);
  vehiclesRef.current = vehicles;

  const handleTimelineBodyScroll = useCallback((event) => {
    if (timelineHeaderRef.current) {
      timelineHeaderRef.current.scrollLeft = event.currentTarget.scrollLeft;
    }
  }, []);

  // Ermittle welche Fahrzeugtypen tatsächlich existieren
  const existingVehicleTypes = useMemo(() => {
    if (!vehicles || vehicles.length === 0) return new Set([1]);
    const types = new Set();
    vehicles.forEach(v => {
      const vType = Number(v.vehicleType || v.vehicle_type || 1);
      types.add(vType);
    });
    return types;
  }, [vehicles]);
  
  // Ref to accumulate activities from multiple listeners
  const immediateActivitiesRef = useRef(new Map());
  
  // Consolidate all activities whenever individual activity types change
  useEffect(() => {
    const consolidated = [...immediateRideActivities, ...scheduledRideActivities, ...breakActivities, ...offlineActivities];
    // Note: plannedShiftActivities are now shown in fleet utilization timeline instead
    if (process.env.NODE_ENV === 'development') {
      console.debug("ShiftCalendar: Konsolidiere Aktivitäten:", {
        immediate: immediateRideActivities.length,
        scheduled: scheduledRideActivities.length, 
        breaks: breakActivities.length,
        offline: offlineActivities.length,
        plannedShifts: plannedShiftActivities.length,
        total: consolidated.length
      });
    }
    setActivities(consolidated);
  }, [immediateRideActivities, scheduledRideActivities, breakActivities, offlineActivities]);

  // WaitTimeManager Updates werden jetzt von AppDataManager global gesteuert
  // Dieser Effect ist nicht mehr nötig
  
  // Initiale Daten laden (nicht-blockierend — onSnapshot-Listener liefern Echtzeit-Daten)
  useEffect(() => {
    console.log("ShiftCalendar: Starte Datenladen...");

    // Fahrzeuge werden vom Kalender für vehiclesRef gebraucht — nur diesen Fetch abwarten
    // Rides und Drivers werden über onSnapshot-Listener weiter unten geladen
    const dateStr = currentDate.toISOString().split('T')[0];
    dispatch(fetchDrivers({ simplified: true }));
    dispatch(fetchVehicles());
    dispatch(fetchPlannedShifts({ startDate: dateStr, endDate: dateStr }));
    dispatch(startVehiclesStatusListener());
    
    // Update current time every 2 minutes to reduce re-renders
    const timeInterval = setInterval(() => {
      const now = new Date();
      setCurrentTime(now);
      
      // Automatischer Datumswechsel um Mitternacht
      const isToday = currentDate.toDateString() === now.toDateString();
      if (!isToday && currentDate < now) {
        // Wenn currentDate in der Vergangenheit liegt und es ein neuer Tag ist, 
        // wechsle automatisch zum aktuellen Tag
        console.log("ShiftCalendar: Automatischer Datumswechsel erkannt, wechsle zu:", now.toDateString());
        setCurrentDate(new Date(now.getFullYear(), now.getMonth(), now.getDate()));
      }
    }, 120000);
    
    return () => {
      clearInterval(timeInterval);
    };
  }, [dispatch, currentDate]);

  // Initiale Shift-Daten werden direkt vom onSnapshot-Listener (weiter unten) geliefert
  // Kein separater getDoc-Aufruf nötig — spart 2 redundante Firestore-Reads

  // Load break activities when shiftsData and vehicles are available
  useEffect(() => {
    if (!shiftsData) {
      return;
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.debug("ShiftCalendar: Lade Pausen-Aktivitäten...", { 
        hasShiftsData: !!shiftsData,
        vehiclesCount: vehiclesRef.current ? vehiclesRef.current.length : 0 
      });
    }
    
    const newBreakActivities = [];
    
    if (shiftsData.drivers) {
      Object.entries(shiftsData.drivers).forEach(([driverId, driverData]) => {
        const currentShift = getActiveShift(driverData);
        if (currentShift && currentShift.breaks && Array.isArray(currentShift.breaks)) {
          currentShift.breaks.forEach((breakItem) => {
            if (breakItem.startTime) {
              const startTime = breakItem.startTime.toDate();
              const endTime = breakItem.endTime 
                ? breakItem.endTime.toDate() 
                : new Date(startTime.getTime() + ((breakItem.plannedDuration || 30) * 60000));
              
              // Find vehicle for this driver - allow breaks even if no vehicle assigned
              const driverVehicle = vehiclesRef.current ? 
                vehiclesRef.current.find(v => v.current_driver?.id === driverId) : null;
              
              // Always create break activity, even if no vehicle found
              const now = new Date();
              const calculatedStatus = (() => {
                if (breakItem.endTime) {
                  return endTime <= now ? 'completed' : 'active';
                } else {
                  return endTime <= now ? 'completed' : 'active';
                }
              })();
              
              newBreakActivities.push({
                id: `break-${driverId}-${startTime.getTime()}`,
                type: 'break',
                vehicleId: driverVehicle ? driverVehicle.id : `virtual-${driverId}`, // Virtual ID if no vehicle
                startTime,
                endTime,
                endTimePlanned: endTime,
                destination: breakItem.type || 'Pause',
                driverId,
                status: calculatedStatus,
                isDelayed: false,
                plannedDuration: breakItem.plannedDuration
              });
            }
          });
        }
      });
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.debug("ShiftCalendar: Gefundene Pausen-Aktivitäten:", newBreakActivities.length);
    }
    setBreakActivities(newBreakActivities);
  }, [shiftsData, vehicles]); // Add vehicles back as dependency for breaks

  // Offline-Perioden verarbeiten (ähnlich wie Pausen)
  useEffect(() => {
    if (!shiftsData) return;
    
    const newOfflineActivities = [];
    
    if (shiftsData.drivers) {
      Object.entries(shiftsData.drivers).forEach(([driverId, driverData]) => {
        // Prüfe sowohl neue Array-Struktur als auch alte Struktur für Rückwärtskompatibilität
        let shiftsArray = [];
        
        if (driverData.shifts && Array.isArray(driverData.shifts)) {
          // Neue Array-Struktur
          shiftsArray = driverData.shifts;
        } else if (driverData.shiftStart) {
          // Alte Struktur - migriere zu Array-Format für Verarbeitung
          shiftsArray = [{
            shiftNumber: 1,
            shiftStart: driverData.shiftStart,
            shiftEnd: driverData.shiftEnd,
            isActive: driverData.isActive || false,
            breaks: driverData.breaks || [],
            offlinePeriods: [] // Alte Struktur hat keine Offline-Perioden
          }];
        }
        
        // Verarbeite alle Schichten
        shiftsArray.forEach((shift) => {
          if (shift.offlinePeriods && Array.isArray(shift.offlinePeriods)) {
            shift.offlinePeriods.forEach((offlinePeriod) => {
              if (offlinePeriod.offlineStart) {
                const startTime = offlinePeriod.offlineStart.toDate();
                const endTime = offlinePeriod.offlineEnd 
                  ? offlinePeriod.offlineEnd.toDate() 
                  : new Date(); // Wenn noch offline, bis jetzt
                
                // Finde Fahrzeug für diesen Fahrer
                const driverVehicle = vehiclesRef.current ? 
                  vehiclesRef.current.find(v => v.current_driver?.id === driverId) : null;
                
                const calculatedStatus = offlinePeriod.offlineEnd ? 'completed' : 'active';
                
                newOfflineActivities.push({
                  id: `offline-${driverId}-${startTime.getTime()}`,
                  type: 'offline',
                  vehicleId: driverVehicle ? driverVehicle.id : `virtual-${driverId}`,
                  startTime,
                  endTime,
                  endTimePlanned: endTime,
                  destination: `Offline (${offlinePeriod.reason || 'unbekannt'})`,
                  driverId,
                  status: calculatedStatus,
                  isDelayed: false,
                  reason: offlinePeriod.reason || 'manual_logout',
                  offlineReason: offlinePeriod.reason || 'manual_logout'
                });
              }
            });
          }
        });
      });
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.debug("ShiftCalendar: Gefundene Offline-Aktivitäten:", newOfflineActivities.length);
    }
    setOfflineActivities(newOfflineActivities);
  }, [shiftsData, vehicles]);

  // Convert planned shifts to activities for timeline visualization
  useEffect(() => {
    if (!plannedShifts || plannedShifts.length === 0) {
      setPlannedShiftActivities([]);
      return;
    }

    const newPlannedShiftActivities = [];
    const currentDateString = currentDate.toISOString().split('T')[0];

    plannedShifts.forEach(shift => {
      if (shift.status === 'cancelled') return;

      // Check if this shift includes the current date
      const relevantDates = shift.dates.filter(date => date === currentDateString);
      
      relevantDates.forEach(date => {
        const [hours, minutes] = shift.startTime.split(':').map(Number);
        const startTime = new Date(currentDate);
        startTime.setHours(hours, minutes, 0, 0);
        
        const endTime = new Date(startTime);
        endTime.setHours(startTime.getHours() + (shift.plannedDuration || 8));

        // Find vehicle for this shift
        let vehicleId = shift.vehicleId;
        if (!vehicleId && vehicles && vehicles.length > 0) {
          // If no specific vehicle assigned, try to find the driver's current vehicle
          const driverVehicle = vehicles.find(v => v.current_driver?.id === shift.driverId);
          vehicleId = driverVehicle?.id || `virtual-planned-${shift.driverId}`;
        } else if (!vehicleId) {
          vehicleId = `virtual-planned-${shift.driverId}`;
        }

        newPlannedShiftActivities.push({
          id: `planned-shift-${shift.id}-${date}`,
          type: 'planned_shift',
          vehicleId,
          startTime,
          endTime,
          endTimePlanned: endTime,
          destination: `Geplante Schicht`,
          driverId: shift.driverId,
          driverName: shift.driverName || shift.driver?.name || 'Unbekannter Fahrer',
          status: 'planned',
          shiftId: shift.id,
          notes: shift.notes,
          plannedDuration: shift.plannedDuration
        });
      });
    });

    if (process.env.NODE_ENV === 'development') {
      console.debug("ShiftCalendar: Gefundene geplante Schichten:", newPlannedShiftActivities.length);
    }
    
    setPlannedShiftActivities(newPlannedShiftActivities);
  }, [plannedShifts, currentDate, vehicles]);

  // Setup realtime listeners for rides and shifts
  useEffect(() => {
    // Don't block listeners if vehicles are empty - they can load rides independently
    
    const formatDateString = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    const currentDateString = formatDateString(currentDate);
    
    // Listen to shifts data for timeline calculation
    // Lade Shifts sowohl für den aktuellen Tag als auch den Vortag (für rollierende Ansicht)
    const shiftsDocRef = doc(db, 'shifts', currentDateString);
    
    // Berechne Vortag
    const previousDate = new Date(currentDate);
    previousDate.setDate(previousDate.getDate() - 1);
    const previousDateString = formatDateString(previousDate);
    const previousShiftsDocRef = doc(db, 'shifts', previousDateString);
    
    let currentShiftsData = null;
    let previousShiftsData = null;
    
    const updateCombinedShiftsData = () => {
      // Kombiniere Shifts-Daten vom aktuellen Tag und Vortag
      const combinedData = { drivers: {} };
      
      // Füge Previous Day Shifts hinzu
      if (previousShiftsData && previousShiftsData.drivers) {
        Object.entries(previousShiftsData.drivers).forEach(([driverId, driverData]) => {
          combinedData.drivers[driverId] = { ...driverData, _fromPreviousDay: true };
        });
      }
      
      // Füge Current Day Shifts hinzu (überschreibt bei Konflikten)
      if (currentShiftsData && currentShiftsData.drivers) {
        Object.entries(currentShiftsData.drivers).forEach(([driverId, driverData]) => {
          combinedData.drivers[driverId] = { ...driverData, _fromCurrentDay: true };
        });
      }
      
      setRawShiftsData(combinedData);
    };
    
    const unsubscribeCurrentShifts = onSnapshot(shiftsDocRef, (docSnapshot) => {
      currentShiftsData = docSnapshot.exists() ? docSnapshot.data() : null;
      updateCombinedShiftsData();
    });
    
    const unsubscribePreviousShifts = onSnapshot(previousShiftsDocRef, (docSnapshot) => {
      previousShiftsData = docSnapshot.exists() ? docSnapshot.data() : null;
      updateCombinedShiftsData();
    });
    
    // Listen to rides for the selected date - both immediate orders (acceptedTimestamp) and general (timestamp)
    // Erweitere den Zeitbereich um den Vortag für die rollierende Ansicht
    const isToday = currentDate.toDateString() === new Date().toDateString();
    const shouldShowFullDay = showFullDay || !isToday;
    
    let queryStartTime, queryEndTime;
    
    if (shouldShowFullDay) {
      // Volltagsansicht: nur aktueller Tag
      queryStartTime = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
      queryEndTime = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + 1);
    } else {
      // Rollierende Ansicht: erweitere um Vortag wenn nötig
      const currentHour = new Date().getHours();
      if (currentHour < 1) {
        // Wenn es vor 01:00 ist, beginne um 22:00 des Vortags
        queryStartTime = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() - 1, 22, 0, 0);
      } else {
        // Sonst beginne um (currentHour - 1) des aktuellen Tages
        queryStartTime = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), currentHour - 1, 0, 0);
      }
      
      // Ende: 12 Stunden nach aktuellem Zeitpunkt
      const endHour = currentHour + 12;
      if (endHour >= 24) {
        queryEndTime = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + 1, endHour - 24, 0, 0);
      } else {
        queryEndTime = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), endHour, 0, 0);
      }
    }
    
    const startOfDay = queryStartTime;
    const endOfDay = queryEndTime;
    
    // Query for immediate orders with acceptedTimestamp
    const immediateRidesQuery = query(
      collection(db, 'rides'),
      where('acceptedTimestamp', '>=', startOfDay),
      where('acceptedTimestamp', '<', endOfDay),
      where('status', 'in', ['accepted', 'arrived', 'in_progress', 'completed', 'cancelled', 'cancelled_by_driver'])
    );
    
    // Query for other rides with timestamp
    const generalRidesQuery = query(
      collection(db, 'rides'),
      where('timestamp', '>=', startOfDay),
      where('timestamp', '<', endOfDay)
    );
    
    // Listen to all scheduled rides for the selected date (pre-bookings with scheduledTime)
    // Erweitere den Zeitraum für die Query wenn wir im 12-Stunden-Fenster sind
    
    let queryStartDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
    let queryEndDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + 1);
    
    // Wenn wir heute sind und nicht im Volltagsmodus, erweitere die Query auf die nächsten 12 Stunden
    if (isToday && !shouldShowFullDay) {
      const currentHour = new Date().getHours();
      // Wenn nach 12 Uhr mittags, includiere auch den nächsten Tag
      if (currentHour >= 12) {
        queryEndDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + 2);
      }
    }
    
    const scheduledRidesWithTimeQuery = query(
      collection(db, 'rides'),
      where('status', 'in', ['scheduled', 'pre_accepted', 'accepted', 'arrived', 'in_progress', 'completed', 'cancelled', 'cancelled_by_driver']),
      where('scheduledTime', '>=', queryStartDate),
      where('scheduledTime', '<', queryEndDate)
    );
    
    // Unassigned rides: Nur Fahrten im relevanten Zeitfenster laden (nicht ALLE)
    // Open rides haben kein scheduledTime, daher filtern wir über timestamp
    const unassignedStartDate = new Date(queryStartDate);
    unassignedStartDate.setDate(unassignedStartDate.getDate() - 1); // 1 Tag zurück für offene Fahrten
    const unassignedRidesQuery = query(
      collection(db, 'rides'),
      where('status', 'in', ['scheduled', 'open']),
      where('timestamp', '>=', unassignedStartDate)
    );
    
    const unsubscribeUnassignedRides = onSnapshot(
      unassignedRidesQuery, 
      (snapshot) => {
        try {
          const allUnassignedRides = [];
          
          snapshot.forEach((doc) => {
            try {
              const rideData = doc.data();
              // Check if ride is for current date or within the extended time window
              const isScheduledInWindow = rideData.scheduledTime && 
                rideData.scheduledTime.toDate() >= queryStartDate && 
                rideData.scheduledTime.toDate() < queryEndDate;
              const isOpenInWindow = rideData.timestamp && 
                rideData.timestamp.toDate() >= queryStartDate && 
                rideData.timestamp.toDate() < queryEndDate;
              
              if ((isScheduledInWindow || isOpenInWindow) && rideData.estimatedTimeMinutes) {
                const startTime = rideData.scheduledTime ? 
                  rideData.scheduledTime.toDate() : 
                  rideData.timestamp.toDate();
                const endTime = new Date(startTime.getTime() + (rideData.estimatedTimeMinutes * 60000));
                
                allUnassignedRides.push({
                  id: doc.id,
                  startTime,
                  endTime,
                  startAddress: getRideStartAddress(rideData),
                  destination: getRideDestinationAddress(rideData) || 'Unbekanntes Ziel',
                  customerName: rideData.customerName || rideData.customer_name,
                  vehicleType: rideData.vehicleType || rideData.vehicle_type,
                  specialCapabilities: getRideSpecialCapabilities(rideData),
                  notes: rideData.notes || null,
                  status: rideData.status
                });
              }
            } catch (error) {
              console.error('Error processing unassigned ride document:', error);
            }
          });
          
          setScheduledRides(allUnassignedRides);
        } catch (error) {
          console.error('Error in unassigned rides listener:', error);
        }
      },
      (error) => {
        console.error('Error listening to unassigned rides:', error);
      }
    );
    
    // Combined handler for both immediate and general rides
    const handleRidesSnapshot = (snapshot, queryType) => {
      snapshot.forEach((doc) => {
        const rideData = doc.data();
        
        // Für Sofortbestellungen mit acceptedTimestamp
        if (rideData.acceptedTimestamp && rideData.driverId && rideData.estimatedTimeMinutes) {
          const startTime = rideData.acceptedTimestamp.toDate();
          const endTime = new Date(startTime.getTime() + (rideData.estimatedTimeMinutes * 60000));
          
          // Finde das Fahrzeug des Fahrers wenn nicht direkt verfügbar
          let vehicleId = rideData.vehicleId;
          if (!vehicleId && rideData.driverId && vehiclesRef.current) {
            const driverVehicle = vehiclesRef.current.find(v => v.current_driver?.id === rideData.driverId);
            vehicleId = driverVehicle?.id;
          }
          
          // Create activity even if no vehicle found (use virtual ID)
          const activity = {
            id: doc.id,
            type: 'ride',
            vehicleId: vehicleId || `virtual-ride-${rideData.driverId}`,
            startTime,
            endTime: rideData.completedTimestamp ? rideData.completedTimestamp.toDate() : endTime,
            endTimePlanned: endTime,
            startAddress: getRideStartAddress(rideData),
            destination: getRideDestinationAddress(rideData) || 'Unbekanntes Ziel',
            driverId: rideData.driverId,
            status: rideData.status,
            isDelayed: rideData.status !== 'completed' && new Date() > endTime,
            customerName: rideData.customerName || rideData.customer_name,
            notes: rideData.notes || null,
            vehicleType: rideData.vehicleType || rideData.vehicle_type || null,
            specialCapabilities: getRideSpecialCapabilities(rideData),
            orderType: rideData.orderType,
            estimatedTimeMinutes: rideData.estimatedTimeMinutes, // Fahrtdauer hinzufügen
            // Roh-Timestamps für die tatsächlich gefahrene Dauer (getActualRideDurationMinutes)
            passengerPickedUpTimestamp: rideData.passengerPickedUpTimestamp || null,
            startedAt: rideData.startedAt || null,
            completedTimestamp: rideData.completedTimestamp || rideData.completionTimestamp || null
          };

          immediateActivitiesRef.current.set(doc.id, activity);
        }
      });
      
      // Update immediate ride activities with all accumulated activities
      const allActivities = Array.from(immediateActivitiesRef.current.values());
      setImmediateRideActivities(allActivities);
    };
    
    // Clear existing activities for this date
    immediateActivitiesRef.current.clear();
    
    // Set up listeners for both queries
    const unsubscribeImmediateRides = onSnapshot(immediateRidesQuery, (snapshot) => 
      handleRidesSnapshot(snapshot, 'immediate')
    );
    
    const unsubscribeGeneralRides = onSnapshot(generalRidesQuery, (snapshot) => 
      handleRidesSnapshot(snapshot, 'general')
    );
    
    // Listen to all scheduled rides (pre-bookings with scheduledTime)
    const unsubscribeScheduledRidesWithTime = onSnapshot(scheduledRidesWithTimeQuery, (snapshot) => {
      const scheduledActivities = [];
      
      snapshot.forEach((doc) => {
        const rideData = doc.data();
        
        if (rideData.scheduledTime && rideData.driverId && rideData.estimatedTimeMinutes) {
          // Überprüfe, ob die Fahrt im aktuell sichtbaren Zeitfenster liegt
          const scheduledTime = rideData.scheduledTime.toDate();
          
          // Berechne das sichtbare Zeitfenster basierend auf aktueller Ansicht
          const currentHour = new Date().getHours();
          const isCurrentlyToday = currentDate.toDateString() === new Date().toDateString();
          const shouldShowFullDayNow = showFullDay || !isCurrentlyToday;
          
          let windowStart, windowEnd;
          
          if (shouldShowFullDayNow) {
            // Volltagsansicht: 00:00 - 23:59
            windowStart = new Date(currentDate);
            windowStart.setHours(0, 0, 0, 0);
            
            windowEnd = new Date(currentDate);
            windowEnd.setHours(23, 59, 59, 999);
          } else {
            // Rollierende Ansicht: 1 Stunde zurück + 12 Stunden voraus
            const startHour = Math.max(0, currentHour - 1);
            const endHour = startHour + 13; // 13 Stunden Fenster
            
            windowStart = new Date(currentDate);
            windowStart.setHours(startHour, 0, 0, 0);
            
            windowEnd = new Date(currentDate);
            if (endHour >= 24) {
              // Über Mitternacht hinaus
              windowEnd.setDate(windowEnd.getDate() + 1);
              windowEnd.setHours(endHour - 24, 0, 0, 0);
            } else {
              windowEnd.setHours(endHour, 0, 0, 0);
            }
          }
          
          // Nur Fahrten anzeigen, die im sichtbaren Zeitfenster beginnen
          if (scheduledTime < windowStart || scheduledTime >= windowEnd) {
            return; // Skip this ride
          }
          
          // Prüfe ob Anfahrtszeit benötigt wird (nur bei pre_accepted Status)
          const needsApproachTime = rideData.status === 'pre_accepted';
          const approachTimeMinutes = 10; // 10 Minuten Anfahrtszeit
          
          // Berechne Startzeit basierend auf Status
          let actualStartTime, approachStartTime, mainStartTime;
          
          if (rideData.startedAt) {
            // Fahrt bereits gestartet - verwende tatsächliche Startzeit
            actualStartTime = rideData.startedAt.toDate();
            mainStartTime = scheduledTime;
            approachStartTime = needsApproachTime ? 
              new Date(scheduledTime.getTime() - (approachTimeMinutes * 60000)) : actualStartTime;
          } else if (needsApproachTime) {
            // Pre-accepted: Anfahrtszeit einplanen
            approachStartTime = new Date(scheduledTime.getTime() - (approachTimeMinutes * 60000));
            mainStartTime = scheduledTime;
            actualStartTime = approachStartTime; // Aktivität beginnt mit Anfahrt
          } else {
            // Andere Status: wie bisher
            actualStartTime = scheduledTime;
            mainStartTime = scheduledTime;
            approachStartTime = null;
          }
          
          const endTime = new Date(mainStartTime.getTime() + (rideData.estimatedTimeMinutes * 60000));
          
          // Finde das Fahrzeug des Fahrers
          const driverVehicle = vehiclesRef.current ? 
            vehiclesRef.current.find(v => v.current_driver?.id === rideData.driverId) : null;
          
          // Create activity even if no vehicle found (use virtual ID)
          scheduledActivities.push({
            id: doc.id,
            type: 'ride',
            vehicleId: driverVehicle ? driverVehicle.id : `virtual-scheduled-${rideData.driverId}`,
            startTime: actualStartTime, // Gesamtstart (inklusive Anfahrt)
            endTime: rideData.completedTimestamp ? rideData.completedTimestamp.toDate() : endTime,
            endTimePlanned: endTime,
            startAddress: getRideStartAddress(rideData),
            destination: getRideDestinationAddress(rideData) || 'Unbekanntes Ziel',
            driverId: rideData.driverId,
            status: rideData.status,
            isDelayed: !['completed', 'cancelled', 'cancelled_by_driver'].includes(rideData.status) && new Date() > endTime,
            customerName: rideData.customerName || rideData.customer_name,
            notes: rideData.notes || null,
            vehicleType: rideData.vehicleType || rideData.vehicle_type || null,
            specialCapabilities: getRideSpecialCapabilities(rideData),
            orderType: rideData.orderType,
            estimatedTimeMinutes: rideData.estimatedTimeMinutes, // Fahrtdauer hinzufügen
            // Roh-Timestamps für die tatsächlich gefahrene Dauer (getActualRideDurationMinutes)
            passengerPickedUpTimestamp: rideData.passengerPickedUpTimestamp || null,
            startedAt: rideData.startedAt || null,
            completedTimestamp: rideData.completedTimestamp || rideData.completionTimestamp || null,
            // Neue Eigenschaften für Anfahrtszeit
            hasApproachTime: needsApproachTime,
            approachStartTime: approachStartTime,
            mainStartTime: mainStartTime,
            approachTimeMinutes: needsApproachTime ? approachTimeMinutes : 0
          });
        }
      });
      
      // Update scheduled ride activities directly (consolidated in useEffect above)
      setScheduledRideActivities(scheduledActivities);
    });
    
    // Note: Break activities are now handled in separate useEffect above for better performance
    
    return () => {
      unsubscribeImmediateRides();
      unsubscribeGeneralRides();
      unsubscribeScheduledRidesWithTime();
      unsubscribeUnassignedRides();
      unsubscribeCurrentShifts();
      unsubscribePreviousShifts();
    };
  }, [currentDate, showFullDay]); // showFullDay hinzugefügt, damit Listener bei Änderung neu aufgebaut werden

  // Helper functions
  const formatTime = (date) => {
    if (!date) return '--:--';
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  // Helper function to get human-readable offline reason text
  const getOfflineReasonText = (reason) => {
    switch (reason) {
      case 'manual_logout':
        return 'Manual abgemeldet';
      case 'app_closed':
        return 'App geschlossen';
      case 'connection_lost':
        return 'Verbindung verloren';
      default:
        return 'Unbekannter Grund';
    }
  };

  // Helper function to calculate and format offline duration
  const calculateOfflineDuration = (startTime, endTime) => {
    const end = endTime || new Date(); // If still offline, use current time
    const durationMs = end - startTime;
    const durationMinutes = Math.floor(durationMs / (1000 * 60));
    
    if (durationMinutes < 60) {
      return `${durationMinutes} Min`;
    } else {
      const hours = Math.floor(durationMinutes / 60);
      const minutes = durationMinutes % 60;
      return minutes > 0 ? `${hours}h ${minutes}min` : `${hours}h`;
    }
  };

  const driversById = useMemo(() => {
    const map = new Map();
    (drivers || []).forEach((driver) => {
      if (driver?.id) map.set(driver.id, driver);
    });
    return map;
  }, [drivers]);

  const vehicleByDriverId = useMemo(() => {
    const map = new Map();
    (vehicles || []).forEach((vehicle) => {
      const driverId = vehicle.driverId || vehicle.current_driver?.id || vehicle.current_driver_id;
      if (driverId) map.set(driverId, vehicle);
    });
    return map;
  }, [vehicles]);

  const activitiesByDriverId = useMemo(() => {
    const map = new Map();
    activities.forEach((activity) => {
      if (!activity.driverId) return;
      const driverActivities = map.get(activity.driverId) || [];
      driverActivities.push(activity);
      map.set(activity.driverId, driverActivities);
    });
    return map;
  }, [activities]);

  const activitiesByVehicleId = useMemo(() => {
    const map = new Map();
    activities.forEach((activity) => {
      if (!activity.vehicleId) return;
      const vehicleActivities = map.get(activity.vehicleId) || [];
      vehicleActivities.push(activity);
      map.set(activity.vehicleId, vehicleActivities);
    });
    return map;
  }, [activities]);

  const getActivitiesForCalendarRow = useCallback((row) => {
    if (row.type === 'driver') {
      return activitiesByDriverId.get(row.driverId) || [];
    }

    const directActivities = activitiesByVehicleId.get(row.vehicleId) || [];
    const driverActivities = row.driverId ? (activitiesByDriverId.get(row.driverId) || []) : [];
    const merged = new Map();

    directActivities.forEach((activity) => merged.set(activity.id, activity));
    driverActivities
      .filter((activity) => activity.vehicleId && String(activity.vehicleId).startsWith('virtual-'))
      .forEach((activity) => merged.set(activity.id, activity));

    return Array.from(merged.values());
  }, [activitiesByDriverId, activitiesByVehicleId]);

  const plannedShiftDateKey = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const day = String(currentDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, [currentDate]);

  const driverRows = useMemo(() => {
    const driverIds = new Set();

    (drivers || []).forEach((driver) => {
      if (driver?.id) driverIds.add(driver.id);
    });

    if (shiftsData?.drivers) {
      Object.keys(shiftsData.drivers).forEach((driverId) => driverIds.add(driverId));
    }

    (vehicles || []).forEach((vehicle) => {
      const driverId = vehicle.driverId || vehicle.current_driver?.id || vehicle.current_driver_id;
      if (driverId) driverIds.add(driverId);
    });

    activities.forEach((activity) => {
      if (activity.driverId) driverIds.add(activity.driverId);
    });

    const now = currentTime;
    const blockingStatuses = ['pre_accepted', 'accepted', 'arrived', 'in_progress'];

    return Array.from(driverIds).map((driverId) => {
      const driver = driversById.get(driverId);
      const shiftData = shiftsData?.drivers?.[driverId];
      const activeShift = getActiveShift(shiftData);
      const vehicle = vehicleByDriverId.get(driverId);
      const vehicleDriver = vehicle?.current_driver?.id === driverId ? vehicle.current_driver : null;
      const rowActivities = activitiesByDriverId.get(driverId) || [];
      const plannedToday = (plannedShifts || []).some((shift) => (
        shift.driverId === driverId &&
        shift.status !== 'cancelled' &&
        Array.isArray(shift.dates) &&
        shift.dates.includes(plannedShiftDateKey)
      ));

      const hasActiveRide = rowActivities.some((activity) => (
        activity.type === 'ride' &&
        blockingStatuses.includes(activity.status) &&
        activity.startTime <= now &&
        activity.endTime > now
      ));
      const hasUpcomingPreorder = rowActivities.some((activity) => (
        activity.type === 'ride' &&
        activity.orderType === 1 &&
        blockingStatuses.includes(activity.status) &&
        activity.startTime > now
      ));
      const isInBreak = rowActivities.some((activity) => (
        activity.type === 'break' &&
        activity.startTime <= now &&
        activity.endTime > now
      ));
      const isOfflineNow = rowActivities.some((activity) => (
        activity.type === 'offline' &&
        activity.startTime <= now &&
        activity.endTime > now
      ));

      let priority = 6;
      if (hasActiveRide) priority = 1;
      else if (hasUpcomingPreorder) priority = 2;
      else if (isInBreak) priority = 4;
      else if (driver?.isOnline || driver?.isAvailable || activeShift?.isActive) priority = 3;
      else if (isOfflineNow || plannedToday) priority = 5;

      const driverName = getDriverName(driver || shiftData || vehicleDriver);
      const driverNumber = driver?.driverNumber || shiftData?.driverNumber || vehicleDriver?.driverNumber || '';
      const vehicleLabel = vehicle ? getVehicleLabel(vehicle) : 'Fahrzeug noch nicht sicher';

      return {
        id: `driver-${driverId}`,
        type: 'driver',
        driverId,
        vehicleId: vehicle?.id || null,
        vehicle,
        driver,
        badgeLabel: vehicle?.vehicleNumber || driverNumber || null,
        primaryLabel: driverName,
        secondaryLabel: vehicleLabel,
        sortPriority: priority,
        sortNumber: normalizeSortableNumber(driverNumber || vehicle?.vehicleNumber),
        sortLabel: driverName
      };
    }).sort((a, b) => (
      a.sortPriority - b.sortPriority ||
      a.sortNumber - b.sortNumber ||
      compareLabels(a.sortLabel, b.sortLabel)
    ));
  }, [
    activities,
    activitiesByDriverId,
    currentTime,
    drivers,
    driversById,
    plannedShiftDateKey,
    plannedShifts,
    shiftsData,
    vehicleByDriverId,
    vehicles
  ]);

  // Berechne Slot-Breite basierend auf Zoom-Level
  const getSlotWidth = useCallback(() => {
    switch (zoomLevel) {
      case '3h': return 480; // 480px pro Stunde (4x zoom)
      case '6h': return 240; // 240px pro Stunde (2x zoom)
      case '12h': 
      default: return 120; // 120px pro Stunde (standard)
    }
  }, [zoomLevel]);

  const getTimeSlots = useCallback(() => {
    const slots = [];
    const currentHour = currentTime.getHours();
    const isToday = currentDate.toDateString() === new Date().toDateString();
    const shouldShowFullDay = showFullDay || !isToday;
    
    if (shouldShowFullDay) {
      // Volltagsansicht: 00:00 - 23:00 (für andere Tage oder manuell aktiviert)
      for (let hour = 0; hour < 24; hour++) {
        slots.push({
          hour,
          displayHour: hour,
          time: `${hour.toString().padStart(2, '0')}:00`,
          isCurrentTime: hour === currentHour && isToday,
          date: new Date(currentDate)
        });
      }
    } else {
      // Rollierende Ansicht basierend auf Zoom-Level
      const hoursToShow = parseInt(zoomLevel); // '3h' -> 3, '6h' -> 6, '12h' -> 12
      const startHour = currentHour - 1; // Erlaube negative Werte für Vortag
      const totalHours = hoursToShow + 1; // +1 für die vergangene Stunde
      
      for (let i = 0; i < totalHours; i++) {
        const hour = startHour + i;
        const displayHour = ((hour % 24) + 24) % 24; // Korrekte Modulo für negative Zahlen
        const dayOffset = Math.floor(hour / 24);
        
        // Berechne das korrekte Datum für diesen Slot
        const slotDate = new Date(currentDate);
        slotDate.setDate(slotDate.getDate() + dayOffset);
        
        slots.push({
          hour: displayHour,
          displayHour,
          absoluteHour: hour, // Für Berechnungen über Mitternacht hinaus
          time: `${displayHour.toString().padStart(2, '0')}:00`,
          isCurrentTime: displayHour === currentHour && 
                        slotDate.toDateString() === new Date().toDateString(),
          date: slotDate,
          isNextDay: dayOffset > 0,
          isPreviousDay: dayOffset < 0
        });
      }
    }
    
    return slots;
  }, [currentTime, currentDate, showFullDay, zoomLevel]);

  const calculateActivityPosition = useCallback((activity) => {
    const timeSlots = getTimeSlots();
    if (timeSlots.length === 0) return { left: '0px', width: '2px' };
    
    const firstSlot = timeSlots[0];
    const baseTime = new Date(firstSlot.date);
    baseTime.setHours(firstSlot.displayHour, 0, 0, 0);
    
    // Berechne Minuten seit dem ersten angezeigten Zeitslot
    // Verwende Math.max(0, ...) nicht, da negative Werte möglich sind (für vergangene Zeiten)
    const startMinutesFromBeginning = (activity.startTime - baseTime) / (1000 * 60);
    const endMinutesFromBeginning = (activity.endTime - baseTime) / (1000 * 60);
    
    // Pixel-basierte Berechnung basierend auf Zoom-Level
    const slotWidth = getSlotWidth();
    const leftPx = (startMinutesFromBeginning / 60) * slotWidth;
    const widthPx = Math.max(2, ((endMinutesFromBeginning - startMinutesFromBeginning) / 60) * slotWidth);
    
    return { left: `${leftPx}px`, width: `${widthPx}px` };
  }, [getTimeSlots]);

  const calculateApproachTimePosition = useCallback((activity) => {
    if (!activity.hasApproachTime || !activity.approachStartTime) {
      return { left: '0px', width: '0px' };
    }
    
    const timeSlots = getTimeSlots();
    if (timeSlots.length === 0) return { left: '0px', width: '0px' };
    
    const firstSlot = timeSlots[0];
    const baseTime = new Date(firstSlot.date);
    baseTime.setHours(firstSlot.displayHour, 0, 0, 0);
    
    // Berechne Position für Anfahrtszeit
    const approachStartMinutes = (activity.approachStartTime - baseTime) / (1000 * 60);
    const mainStartMinutes = (activity.mainStartTime - baseTime) / (1000 * 60);
    
    const slotWidth = getSlotWidth();
    const leftPx = (approachStartMinutes / 60) * slotWidth;
    const widthPx = Math.max(2, ((mainStartMinutes - approachStartMinutes) / 60) * slotWidth);
    
    return { left: `${leftPx}px`, width: `${widthPx}px` };
  }, [getTimeSlots]);

  const calculateMainActivityPosition = useCallback((activity) => {
    if (!activity.hasApproachTime || !activity.mainStartTime) {
      return calculateActivityPosition(activity);
    }
    
    const timeSlots = getTimeSlots();
    if (timeSlots.length === 0) return { left: '0px', width: '2px' };
    
    const firstSlot = timeSlots[0];
    const baseTime = new Date(firstSlot.date);
    baseTime.setHours(firstSlot.displayHour, 0, 0, 0);
    
    // Berechne Position für Hauptfahrt (ohne Anfahrtszeit)
    const mainStartMinutes = (activity.mainStartTime - baseTime) / (1000 * 60);
    const endMinutesFromBeginning = (activity.endTime - baseTime) / (1000 * 60);
    
    const slotWidth = getSlotWidth();
    const leftPx = (mainStartMinutes / 60) * slotWidth;
    const widthPx = Math.max(2, ((endMinutesFromBeginning - mainStartMinutes) / 60) * slotWidth);
    
    return { left: `${leftPx}px`, width: `${widthPx}px` };
  }, [getTimeSlots, calculateActivityPosition]);

  const getCurrentTimePosition = useCallback(() => {
    const timeSlots = getTimeSlots();
    if (timeSlots.length === 0) return 0;
    
    const firstSlot = timeSlots[0];
    const baseTime = new Date(firstSlot.date);
    baseTime.setHours(firstSlot.displayHour, 0, 0, 0);
    
    // Berechne Minuten seit dem ersten angezeigten Zeitslot
    // Entferne Math.max(0, ...) da negative Werte bei vergangenen Zeiten auftreten können
    const minutesFromBeginning = (currentTime - baseTime) / (1000 * 60);
    
    // Pixel-basierte Berechnung basierend auf Zoom-Level
    const slotWidth = getSlotWidth();
    const totalPositionPx = (minutesFromBeginning / 60) * slotWidth;
    
    return totalPositionPx;
  }, [getTimeSlots, currentTime]);

  const getShiftStartMarkers = (row) => {
    const driverId = row.driverId || row.vehicle?.current_driver?.id;
    if (!driverId || !shiftsData || !shiftsData.drivers) {
      return [];
    }

    const driverData = shiftsData.drivers[driverId];
    const currentShift = getActiveShift(driverData);
    
    if (!currentShift || !currentShift.shiftStart) {
      return [];
    }

    const timeSlots = getTimeSlots();
    if (timeSlots.length === 0) return [];
    
    const firstSlot = timeSlots[0];
    const baseTime = new Date(firstSlot.date);
    baseTime.setHours(firstSlot.displayHour, 0, 0, 0);
    
    const shiftStart = currentShift.shiftStart.toDate();
    
    // Prüfe ob der Schichtbeginn im angezeigten Zeitraum liegt
    const lastSlot = timeSlots[timeSlots.length - 1];
    const endTime = new Date(lastSlot.date);
    endTime.setHours(lastSlot.displayHour + 1, 0, 0, 0);
    
    if (shiftStart < baseTime || shiftStart > endTime) {
      return [];
    }
    
    // Berechne Position
    const minutesFromBeginning = (shiftStart - baseTime) / (1000 * 60);
    const slotWidth = getSlotWidth();
    const leftPx = (minutesFromBeginning / 60) * slotWidth;
    
    return [{
      time: shiftStart,
      position: leftPx,
      driverName: row.primaryLabel,
      vehicleModel: row.vehicle?.model || row.vehicle?.name || 'Fahrzeug noch nicht sicher'
    }];
  };

  const getShiftEndMarkers = (row) => {
    const driverId = row.driverId || row.vehicle?.current_driver?.id;
    if (!driverId || !shiftsData || !shiftsData.drivers) {
      return [];
    }

    const driverData = shiftsData.drivers[driverId];
    const currentShift = getActiveShift(driverData);
    
    if (!currentShift || !currentShift.shiftEnd) {
      return [];
    }

    const timeSlots = getTimeSlots();
    if (timeSlots.length === 0) return [];
    
    const firstSlot = timeSlots[0];
    const baseTime = new Date(firstSlot.date);
    baseTime.setHours(firstSlot.displayHour, 0, 0, 0);
    
    const shiftEnd = currentShift.shiftEnd.toDate();
    
    // Prüfe ob das Schichtende im angezeigten Zeitraum liegt
    const lastSlot = timeSlots[timeSlots.length - 1];
    const endTime = new Date(lastSlot.date);
    endTime.setHours(lastSlot.displayHour + 1, 0, 0, 0);
    
    if (shiftEnd < baseTime || shiftEnd > endTime) {
      return [];
    }
    
    // Berechne Position
    const minutesFromBeginning = (shiftEnd - baseTime) / (1000 * 60);
    const slotWidth = getSlotWidth();
    const leftPx = (minutesFromBeginning / 60) * slotWidth;
    
    return [{
      time: shiftEnd,
      position: leftPx,
      driverName: row.primaryLabel,
      vehicleModel: row.vehicle?.model || row.vehicle?.name || 'Fahrzeug noch nicht sicher'
    }];
  };

  const calculateScheduledRidesTimeline = () => {
    const timeSlots = getTimeSlots();
    if (timeSlots.length === 0 || scheduledRides.length === 0) return [];
    
    const firstSlot = timeSlots[0];
    const baseTime = new Date(firstSlot.date);
    baseTime.setHours(firstSlot.displayHour, 0, 0, 0);
    
    // Filtere erst rides mit gültigen Zeiten, dann sortiere
    const validRides = scheduledRides.filter(ride => ride.startTime && ride.endTime);
    const sortedRides = [...validRides].sort((a, b) => a.startTime - b.startTime);
    
    // Erstelle Timeline mit Überlappungen
    const ridesWithPositions = sortedRides.map((ride, rideIndex) => {
      // Berechne Minuten seit dem ersten angezeigten Zeitslot
      // Entferne Math.max(0, ...) da negative Werte bei vergangenen Zeiten auftreten können
      const startMinutesFromBeginning = (ride.startTime - baseTime) / (1000 * 60);
      const endMinutesFromBeginning = (ride.endTime - baseTime) / (1000 * 60);
      
      // Pixel-basierte Berechnung basierend auf Zoom-Level
      const slotWidth = getSlotWidth();
      const leftPx = (startMinutesFromBeginning / 60) * slotWidth;
      const widthPx = Math.max(2, ((endMinutesFromBeginning - startMinutesFromBeginning) / 60) * slotWidth);
      
      // Finde Überlappungen mit vorherigen Fahrten
      let overlaps = false;
      for (let i = 0; i < rideIndex; i++) {
        const otherRide = sortedRides[i];
        if (ride.startTime < otherRide.endTime && ride.endTime > otherRide.startTime) {
          overlaps = true;
          break;
        }
      }
      
      // Wähle Farbe basierend auf Index und Überlappungen
      const colors = ['#424242', '#616161', '#757575', '#9e9e9e', '#bdbdbd'];
      const color = overlaps ? colors[rideIndex % colors.length] : '#000000';
      
      return {
        ...ride,
        left: leftPx,
        width: widthPx,
        color: color,
        overlaps: overlaps
      };
    });
    
    return ridesWithPositions;
  };

  const memoizedTimeSlots = useMemo(() => getTimeSlots(), [getTimeSlots]);

  const availabilityTimeline = useMemo(() => {
    const timeSlots = memoizedTimeSlots;
    if (timeSlots.length === 0) return { segments: [] };
    
    const firstSlot = timeSlots[0];
    const lastSlot = timeSlots[timeSlots.length - 1];
    
    const baseTime = new Date(firstSlot.date);
    baseTime.setHours(firstSlot.displayHour, 0, 0, 0);
    
    const endTime = new Date(lastSlot.date);
    endTime.setHours(lastSlot.displayHour + 1, 0, 0, 0);
    
    const totalMinutes = (endTime - baseTime) / (1000 * 60);
    
    // Hole Schichtdaten für das aktuelle Datum
    const formatDateString = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    const currentDateString = formatDateString(currentDate);
    
    // Development only logging - removed excessive timeline logging
    if (process.env.NODE_ENV === 'development') {
      console.debug(`Timeline-Berechnung gestartet:`, { timeSlots: timeSlots.length, totalMinutes });
    }
    
    // Erstelle eine Timeline mit minütlicher Auflösung
    const timeline = [];
    
    for (let minute = 0; minute < totalMinutes; minute++) {
      const checkTime = new Date(baseTime.getTime() + minute * 60000);
      
      let availableCount = 0;
      let totalActiveDrivers = 0;
      
      if (shiftsData && shiftsData.drivers) {
        Object.entries(shiftsData.drivers).forEach(([driverId, driverData]) => {
          // Prüfe ob der Fahrer zu dieser Zeit eine aktive Schicht hat
          const currentShift = getActiveShift(driverData);
          if (!currentShift) return;
          
          const shiftStart = currentShift.shiftStart?.toDate();
          const shiftEnd = currentShift.shiftEnd?.toDate();
          const isShiftActive = currentShift.isActive;
          
          // Schicht muss aktiv sein und zur aktuellen Zeit laufen
          if (!isShiftActive || !shiftStart || checkTime < shiftStart) {
            return;
          }
          
          if (shiftEnd && checkTime > shiftEnd) {
            return;
          }
          
          totalActiveDrivers++;
          
          // Prüfe ob der Fahrer in einer Pause ist
          const isInBreak = currentShift.breaks && currentShift.breaks.some(breakItem => {
            if (!breakItem.startTime) return false;
            
            const breakStart = breakItem.startTime.toDate();
            const breakEnd = breakItem.endTime ? breakItem.endTime.toDate() : 
              new Date(breakStart.getTime() + ((breakItem.plannedDuration || 30) * 60000));
            
            // Prüfe ob die Pause zur aktuellen Zeit aktiv ist
            return breakStart <= checkTime && breakEnd > checkTime;
          });
          
          // Prüfe aktive Fahrten aus rides-Sammlung für diesen Fahrer
          // Nur verbindlich zugewiesene Fahrten blockieren den Fahrer
          // 'scheduled' und 'open' sind unverbindlich und werden in "Offene Vorbestellungen" verwaltet
          const isOnRide = activities.some(activity => {
            if (activity.type !== 'ride' || activity.driverId !== driverId) {
              return false;
            }
            
            // Nur verbindlich zugewiesene Status blockieren den Fahrer
            const blockingStatuses = ['pre_accepted', 'accepted', 'arrived', 'in_progress'];
            if (!blockingStatuses.includes(activity.status)) {
              return false;
            }
            
            // Berücksichtige Anfahrtszeit bei pre_accepted Status
            const effectiveStartTime = (activity.status === 'pre_accepted' && activity.hasApproachTime) ? 
              activity.approachStartTime : activity.startTime;
            
            return effectiveStartTime <= checkTime && activity.endTime > checkTime;
          });
          
          if (!isInBreak && !isOnRide) {
            availableCount++;
          }
        });
      }
      
      // Prüfe ob verfügbare Fahrer noch innerhalb ihrer 8-Stunden-Schicht sind
      let availableWithinShiftHours = 0;
      let availableOverShiftHours = 0;
      
      if (shiftsData && shiftsData.drivers) {
        Object.entries(shiftsData.drivers).forEach(([driverId, driverData]) => {
          const currentShift = getActiveShift(driverData);
          if (!currentShift) return;
          
          const shiftStart = currentShift.shiftStart?.toDate();
          const shiftEnd = currentShift.shiftEnd?.toDate();
          const isShiftActive = currentShift.isActive;
          
          if (!isShiftActive || !shiftStart || checkTime < shiftStart) {
            return;
          }
          
          if (shiftEnd && checkTime > shiftEnd) {
            return;
          }
          
          // Berechne 8-Stunden-Grenze ab Schichtbeginn
          const maxShiftTime = new Date(shiftStart.getTime() + (8 * 60 * 60 * 1000));
          
          const isInBreak = currentShift.breaks && currentShift.breaks.some(breakItem => {
            if (!breakItem.startTime) return false;
            const breakStart = breakItem.startTime.toDate();
            const breakEnd = breakItem.endTime ? breakItem.endTime.toDate() : 
              new Date(breakStart.getTime() + ((breakItem.plannedDuration || 30) * 60000));
            return breakStart <= checkTime && breakEnd > checkTime;
          });
          
          const isOnRide = activities.some(activity => {
            if (activity.type !== 'ride' || activity.driverId !== driverId) {
              return false;
            }
            
            // Nur verbindlich zugewiesene Status blockieren den Fahrer
            const blockingStatuses = ['pre_accepted', 'accepted', 'arrived', 'in_progress'];
            if (!blockingStatuses.includes(activity.status)) {
              return false;
            }
            
            // Berücksichtige Anfahrtszeit bei pre_accepted Status
            const effectiveStartTime = (activity.status === 'pre_accepted' && activity.hasApproachTime) ? 
              activity.approachStartTime : activity.startTime;
            
            return effectiveStartTime <= checkTime && activity.endTime > checkTime;
          });
          
          if (!isInBreak && !isOnRide) {
            if (checkTime <= maxShiftTime) {
              availableWithinShiftHours++;
            } else {
              availableOverShiftHours++;
            }
          }
        });
      }
      
      const totalAvailable = availableWithinShiftHours + availableOverShiftHours;
      
      // Check for planned shifts at this time
      let plannedShiftsCount = 0;
      if (plannedShifts && plannedShifts.length > 0) {
        plannedShifts.forEach(shift => {
          if (shift.status === 'cancelled') return;
          
          // Check if this shift includes the current date
          const relevantDates = shift.dates.filter(date => date === currentDateString);
          if (relevantDates.length === 0) return;
          
          const [hours, minutes] = shift.startTime.split(':').map(Number);
          const shiftStart = new Date(currentDate);
          shiftStart.setHours(hours, minutes, 0, 0);
          
          const shiftEnd = new Date(shiftStart);
          shiftEnd.setHours(shiftStart.getHours() + (shift.plannedDuration || 8));
          
          // Check if current time is within planned shift
          if (checkTime >= shiftStart && checkTime < shiftEnd) {
            plannedShiftsCount++;
          }
        });
      }
      
      // Determine status: prioritize planned shifts when available
      let finalStatus;
      if (plannedShiftsCount > 0 && totalAvailable === 0) {
        finalStatus = 'planned'; // Show planned when no actual drivers available
      } else if (availableWithinShiftHours > 0) {
        finalStatus = 'normal';
      } else if (availableOverShiftHours > 0) {
        finalStatus = 'overtime';
      } else {
        finalStatus = 'unavailable';
      }
      
      timeline.push({
        minute,
        time: checkTime,
        available: totalAvailable > 0,
        availableCount: totalAvailable,
        availableWithinShiftHours,
        availableOverShiftHours,
        totalActiveDrivers,
        plannedShiftsCount,
        status: finalStatus
      });
    }
    
    // Gruppiere zusammenhängende Segmente
    const segments = [];
    let currentSegment = null;
    
    timeline.forEach((point) => {
      if (!currentSegment || currentSegment.status !== point.status) {
        if (currentSegment) {
          segments.push(currentSegment);
        }
        currentSegment = {
          startMinute: point.minute,
          endMinute: point.minute + 1,
          available: point.available,
          availableCount: point.availableCount,
          availableWithinShiftHours: point.availableWithinShiftHours,
          availableOverShiftHours: point.availableOverShiftHours,
          totalWithDriver: point.totalActiveDrivers,
          plannedShiftsCount: point.plannedShiftsCount,
          status: point.status,
          startTime: point.time,
          endTime: new Date(point.time.getTime() + 60000)
        };
      } else {
        currentSegment.endMinute = point.minute + 1;
        currentSegment.endTime = new Date(point.time.getTime() + 60000);
        currentSegment.availableCount = point.availableCount;
        currentSegment.availableWithinShiftHours = point.availableWithinShiftHours;
        currentSegment.availableOverShiftHours = point.availableOverShiftHours;
        currentSegment.totalWithDriver = point.totalActiveDrivers;
        currentSegment.plannedShiftsCount = Math.max(currentSegment.plannedShiftsCount || 0, point.plannedShiftsCount);
      }
    });
    
    if (currentSegment) {
      segments.push(currentSegment);
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.debug(`Timeline-Berechnung abgeschlossen:`, { segmentsCount: segments.length });
    }
    
    return { segments };
  }, [memoizedTimeSlots, shiftsData, activities, currentDate, plannedShifts]);


  const calculateTimeToNextUnavailability = () => {
    const now = new Date();
    const { segments } = availabilityTimeline;
    
    if (!segments || segments.length === 0) return null;
    
    // Finde das nächste unavailable Segment nach der aktuellen Zeit
    const nextUnavailableSegment = segments.find(segment => 
      segment.status === 'unavailable' && segment.startTime > now
    );
    
    if (!nextUnavailableSegment) return null;
    
    const minutesUntilUnavailable = Math.ceil((nextUnavailableSegment.startTime - now) / (1000 * 60));
    
    return {
      minutesUntil: minutesUntilUnavailable,
      startTime: nextUnavailableSegment.startTime,
      endTime: nextUnavailableSegment.endTime,
      duration: Math.ceil((nextUnavailableSegment.endTime - nextUnavailableSegment.startTime) / (1000 * 60))
    };
  };

  const findNextAvailableSegment = () => {
    const now = new Date();
    const { segments } = availabilityTimeline;
    
    if (!segments || segments.length === 0) return null;
    
    // Finde das nächste verfügbare Segment (normal oder overtime) nach der aktuellen Zeit
    const nextAvailableSegment = segments.find(segment => 
      (segment.status === 'normal' || segment.status === 'overtime') && 
      segment.startTime > now
    );
    
    if (!nextAvailableSegment) return null;
    
    const minutesUntilAvailable = Math.ceil((nextAvailableSegment.startTime - now) / (1000 * 60));
    
    return {
      minutesUntil: minutesUntilAvailable,
      startTime: nextAvailableSegment.startTime,
      availableCount: nextAvailableSegment.availableCount,
      status: nextAvailableSegment.status
    };
  };

  const calculateCurrentWaitTime = () => {
    if (!shiftsData || !shiftsData.drivers) {
      return { general: "20+ Min", kombi: "20+ Min", minibus: "20+ Min" };
    }

    const now = new Date();
    const { segments } = availabilityTimeline;
    
    if (!segments || segments.length === 0) {
      return { general: "20+ Min", kombi: "20+ Min", minibus: "20+ Min" };
    }

    // Finde aktuelles Segment
    const currentSegment = segments.find(segment => 
      now >= segment.startTime && now < segment.endTime
    );

    // Hilfsfunktion für Aufrundung auf 5-Min-Schritte
    const roundToFiveMinutes = (minutes) => Math.ceil(minutes / 5) * 5;

    // Berechne Wartezeit basierend auf Timeline-Daten
    const calculateWaitTimeFromTimeline = (currentAvailable) => {
      if (currentAvailable > 0) {
        // Fahrer aktuell verfügbar
        const nextUnavailable = calculateTimeToNextUnavailability();
        
        if (!nextUnavailable || nextUnavailable.minutesUntil > 20) {
          // Standard-Wartezeit wenn keine baldige Nicht-Verfügbarkeit
          return "5-10 Min";
        } else {
          // Kurz vor rotem Abschnitt - warte bis nach Ausfallzeit
          const totalWaitTime = nextUnavailable.minutesUntil + nextUnavailable.duration;
          const roundedTime = roundToFiveMinutes(totalWaitTime);
          return `${roundedTime}-${roundedTime + 5} Min (nach Ausfallzeit)`;
        }
      } else {
        // Keine Fahrer verfügbar - finde nächsten verfügbaren Zeitpunkt
        const nextAvailable = findNextAvailableSegment();
        
        if (!nextAvailable) {
          return "30+ Min (keine geplante Verfügbarkeit)";
        }
        
        const roundedTime = roundToFiveMinutes(nextAvailable.minutesUntil);
        return `${roundedTime}-${roundedTime + 5} Min`;
      }
    };

    // Berechne für Standard-Fahrzeuge
    const currentAvailable = currentSegment ? currentSegment.availableCount : 0;
    const generalWaitTime = calculateWaitTimeFromTimeline(currentAvailable);

    // Prüfe erst ob Fahrzeuge des Typs existieren und mit Fahrern besetzt sind
    const availableVehicleTypes = vehiclesRef.current.reduce((acc, vehicle) => {
      if (vehicle.current_driver && vehicle.vehicleType) {
        if (vehicle.vehicleType === 2 || vehicle.vehicleType === 3 || vehicle.vehicleType === 4) acc.grossraum = true;
        if (vehicle.vehicleType === 3 || vehicle.vehicleType === 4) acc.kleinbus7 = true;
        if (vehicle.vehicleType === 4) acc.kleinbus8 = true;
      }
      return acc;
    }, { grossraum: false, kleinbus7: false, kleinbus8: false });

    // Berechne für Großraum (Typ 2) - nur wenn Fahrzeuge verfügbar
    let grossraumWaitTime = "nicht verfügbar";
    if (availableVehicleTypes.grossraum) {
      const grossraumAvailability = calculateVehicleTypeAvailability(2);
      const grossraumSegments = grossraumAvailability.segments;
      const currentGrossraumSegment = grossraumSegments.find(segment =>
        now >= segment.startTime && now < segment.endTime
      );
      grossraumWaitTime = calculateWaitTimeFromTimeline(
        currentGrossraumSegment ? currentGrossraumSegment.availableCount : 0
      );
    }

    // Berechne für Kleinbus 7 (Typ 3)
    let kleinbus7WaitTime = "nicht verfügbar";
    if (availableVehicleTypes.kleinbus7) {
      const kleinbus7Availability = calculateVehicleTypeAvailability(3);
      const kleinbus7Segments = kleinbus7Availability.segments;
      const currentKleinbus7Segment = kleinbus7Segments.find(segment =>
        now >= segment.startTime && now < segment.endTime
      );
      kleinbus7WaitTime = calculateWaitTimeFromTimeline(
        currentKleinbus7Segment ? currentKleinbus7Segment.availableCount : 0
      );
    }

    // Berechne für Kleinbus 8 (Typ 4)
    let kleinbus8WaitTime = "nicht verfügbar";
    if (availableVehicleTypes.kleinbus8) {
      const kleinbus8Availability = calculateVehicleTypeAvailability(4);
      const kleinbus8Segments = kleinbus8Availability.segments;
      const currentKleinbus8Segment = kleinbus8Segments.find(segment =>
        now >= segment.startTime && now < segment.endTime
      );
      kleinbus8WaitTime = calculateWaitTimeFromTimeline(
        currentKleinbus8Segment ? currentKleinbus8Segment.availableCount : 0
      );
    }

    return {
      general: generalWaitTime,
      grossraum: grossraumWaitTime,
      kleinbus7: kleinbus7WaitTime,
      kleinbus8: kleinbus8WaitTime
    };
  };

  const calculateVehicleTypeAvailability = useCallback((vehicleType) => {
    const timeSlots = getTimeSlots();
    if (timeSlots.length === 0) return { segments: [] };
    
    const firstSlot = timeSlots[0];
    const lastSlot = timeSlots[timeSlots.length - 1];
    
    const baseTime = new Date(firstSlot.date);
    baseTime.setHours(firstSlot.displayHour, 0, 0, 0);
    
    const endTime = new Date(lastSlot.date);
    endTime.setHours(lastSlot.displayHour + 1, 0, 0, 0);
    
    const totalMinutes = (endTime - baseTime) / (1000 * 60);
    
    // Filtere Fahrzeuge nach Typ (Kompatibilitätsmatrix)
    const compatibleTypes = getCompatibleVehicleTypes(vehicleType);
    const vehiclesOfType = vehiclesRef.current.filter(v => compatibleTypes.includes(v.vehicleType));
    
    if (process.env.NODE_ENV === 'development') {
      console.debug(`Fahrzeugtyp ${vehicleType} Timeline:`, { vehicleCount: vehiclesOfType.length });
    }
    
    // Erstelle eine Timeline mit minütlicher Auflösung
    const timeline = [];
    
    for (let minute = 0; minute < totalMinutes; minute++) {
      const checkTime = new Date(baseTime.getTime() + minute * 60000);
      
      let availableCount = 0;
      let totalWithDriver = 0;
      let availableType2Count = 0;
      let availableType3Count = 0;
      let availableWithinShiftHours = 0;
      let availableOverShiftHours = 0;
      
      vehiclesOfType.forEach(vehicle => {
        // Prüfe ob das Fahrzeug einen aktiven Fahrer hat
        if (!vehicle.current_driver) {
          return;
        }
        
        const driverId = vehicle.current_driver.id;
        
        // Prüfe ob der Fahrer eine aktive Schicht hat
        if (shiftsData && shiftsData.drivers && shiftsData.drivers[driverId]) {
          const driverData = shiftsData.drivers[driverId];
          const currentShift = getActiveShift(driverData);
          if (!currentShift) return;
          
          const shiftStart = currentShift.shiftStart?.toDate();
          const shiftEnd = currentShift.shiftEnd?.toDate();
          const isShiftActive = currentShift.isActive;
          
          // Schicht muss aktiv sein und zur aktuellen Zeit laufen
          if (!isShiftActive || !shiftStart || checkTime < shiftStart) {
            return;
          }
          
          if (shiftEnd && checkTime > shiftEnd) {
            return;
          }
          
          totalWithDriver++;
          
          // Berechne 8-Stunden-Grenze ab Schichtbeginn
          const maxShiftTime = new Date(shiftStart.getTime() + (8 * 60 * 60 * 1000));
          
          // Prüfe ob der Fahrer in einer Pause ist
          const isInBreak = currentShift.breaks && currentShift.breaks.some(breakItem => {
            if (!breakItem.startTime) return false;
            
            const breakStart = breakItem.startTime.toDate();
            const breakEnd = breakItem.endTime ? breakItem.endTime.toDate() : 
              new Date(breakStart.getTime() + ((breakItem.plannedDuration || 30) * 60000));
            
            return breakStart <= checkTime && breakEnd > checkTime;
          });
          
          // Prüfe aktive Fahrten
          const isOnRide = activities.some(activity => {
            if (activity.type !== 'ride' || activity.vehicleId !== vehicle.id) {
              return false;
            }
            
            // Nur verbindlich zugewiesene Status blockieren das Fahrzeug
            const blockingStatuses = ['pre_accepted', 'accepted', 'arrived', 'in_progress'];
            if (!blockingStatuses.includes(activity.status)) {
              return false;
            }
            
            // Berücksichtige Anfahrtszeit bei pre_accepted Status
            const effectiveStartTime = (activity.status === 'pre_accepted' && activity.hasApproachTime) ? 
              activity.approachStartTime : activity.startTime;
            
            return effectiveStartTime <= checkTime && activity.endTime > checkTime;
          });
          
          if (!isInBreak && !isOnRide) {
            availableCount++;
            if (vehicle.vehicleType === 2) availableType2Count++;
            if (vehicle.vehicleType === 3) availableType3Count++;
            
            if (checkTime <= maxShiftTime) {
              availableWithinShiftHours++;
            } else {
              availableOverShiftHours++;
            }
          }
        }
      });
      
      // Check for planned shifts for this vehicle type at this time
      let plannedShiftsCount = 0;
      if (plannedShifts && plannedShifts.length > 0) {
        const currentDateString = currentDate.toISOString().split('T')[0];
        
        plannedShifts.forEach(shift => {
          if (shift.status === 'cancelled') return;
          
          // Check if this shift includes the current date
          const relevantDates = shift.dates.filter(date => date === currentDateString);
          if (relevantDates.length === 0) return;
          
          // Check if shift has a vehicle of the correct type
          if (shift.vehicleId) {
            const shiftVehicle = vehiclesRef.current.find(v => v.id === shift.vehicleId);
            if (shiftVehicle) {
              const vehicleMatchesType = compatibleTypes.includes(shiftVehicle.vehicleType);
              
              if (!vehicleMatchesType) return;
            }
          }
          
          const [hours, minutes] = shift.startTime.split(':').map(Number);
          const shiftStart = new Date(currentDate);
          shiftStart.setHours(hours, minutes, 0, 0);
          
          const shiftEnd = new Date(shiftStart);
          shiftEnd.setHours(shiftStart.getHours() + (shift.plannedDuration || 8));
          
          // Check if current time is within planned shift
          if (checkTime >= shiftStart && checkTime < shiftEnd) {
            plannedShiftsCount++;
          }
        });
      }
      
      // Determine status: prioritize planned shifts when available
      let finalStatus;
      if (plannedShiftsCount > 0 && availableCount === 0) {
        finalStatus = 'planned'; // Show planned when no actual drivers available
      } else if (availableWithinShiftHours > 0) {
        finalStatus = 'normal';
      } else if (availableOverShiftHours > 0) {
        finalStatus = 'overtime';
      } else {
        finalStatus = 'unavailable';
      }
      
      timeline.push({
        minute,
        time: checkTime,
        available: availableCount > 0,
        availableCount,
        totalWithDriver,
        totalVehicles: vehiclesOfType.length,
        availableType2Count,
        availableType3Count,
        availableWithinShiftHours,
        availableOverShiftHours,
        plannedShiftsCount,
        status: finalStatus
      });
    }
    
    // Gruppiere zusammenhängende Segmente
    const segments = [];
    let currentSegment = null;
    
    timeline.forEach((point) => {
      if (!currentSegment || currentSegment.status !== point.status) {
        if (currentSegment) {
          segments.push(currentSegment);
        }
        currentSegment = {
          startMinute: point.minute,
          endMinute: point.minute + 1,
          available: point.available,
          availableCount: point.availableCount,
          totalWithDriver: point.totalWithDriver,
          totalVehicles: point.totalVehicles,
          availableType2Count: point.availableType2Count,
          availableType3Count: point.availableType3Count,
          availableWithinShiftHours: point.availableWithinShiftHours,
          availableOverShiftHours: point.availableOverShiftHours,
          plannedShiftsCount: point.plannedShiftsCount,
          status: point.status,
          startTime: point.time,
          endTime: new Date(point.time.getTime() + 60000)
        };
      } else {
        currentSegment.endMinute = point.minute + 1;
        currentSegment.endTime = new Date(point.time.getTime() + 60000);
        currentSegment.availableCount = point.availableCount;
        currentSegment.totalWithDriver = point.totalWithDriver;
        currentSegment.availableType2Count = point.availableType2Count;
        currentSegment.availableType3Count = point.availableType3Count;
        currentSegment.availableWithinShiftHours = point.availableWithinShiftHours;
        currentSegment.availableOverShiftHours = point.availableOverShiftHours;
        currentSegment.plannedShiftsCount = Math.max(currentSegment.plannedShiftsCount || 0, point.plannedShiftsCount);
      }
    });
    
    if (currentSegment) {
      segments.push(currentSegment);
    }
    
    return { segments };
  }, [getTimeSlots, shiftsData, activities, currentDate, plannedShifts]);

  // Memoized timeline calculations to prevent recalculation on every render
  const kombiAvailability = useMemo(() => calculateVehicleTypeAvailability(2), [calculateVehicleTypeAvailability]);
  const minibusAvailability = useMemo(() => calculateVehicleTypeAvailability(3), [calculateVehicleTypeAvailability]);

  // Navigation handlers
  const handlePrevious = () => {
    const newDate = new Date(currentDate);
    if (currentView === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setDate(newDate.getDate() - 1);
    }
    setCurrentDate(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);
    if (currentView === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleDateChange = (date) => {
    if (date) {
      setCurrentDate(date.toDate());
    }
  };

  const handleActivityClick = (event, activity) => {
    setSelectedActivity(activity);
    setMenuAnchor(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setSelectedActivity(null);
  };

  // Week overview data
  const getWeekOverviewData = () => {
    const weekDays = [];
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay() + 1); // Monday
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      weekDays.push(day);
    }
    
    return vehiclesRef.current.map(vehicle => {
      const vehicleWeek = weekDays.map(day => {
        // Calculate utilization for this vehicle on this day
        const dayActivities = activities.filter(a => 
          a.vehicleId === vehicle.id && 
          a.startTime.toDateString() === day.toDateString()
        );
        
        const totalMinutes = dayActivities.reduce((sum, activity) => {
          const duration = (activity.endTime - activity.startTime) / (1000 * 60);
          return sum + duration;
        }, 0);
        
        const utilization = Math.min(1, totalMinutes / (8 * 60)); // 8 hour workday
        
        return { day, utilization, hours: Math.round(totalMinutes / 60) };
      });
      
      return { vehicle, week: vehicleWeek };
    });
  };

  // Loading and error states
  const isLoading = vehiclesLoading;
  const error = vehiclesError;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Loading message="Kalender wird geladen..." />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <ErrorMessage
          title="Fehler beim Laden des Kalenders"
          message={error}
          onRetry={() => {
            dispatch(fetchVehicles());
          }}
        />
      </Box>
    );
  }

  // Render Gantt Chart View
  const renderGanttView = () => {
    const timeSlots = memoizedTimeSlots;
    const currentTimePos = getCurrentTimePosition();
    const isToday = currentDate.toDateString() === new Date().toDateString();
    const isAutoFullDay = !isToday;
    
    return (
      <GanttContainer>
        <GanttHeader>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={handlePrevious}>
              <PreviousIcon />
            </IconButton>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={dayjs(currentDate)}
                onChange={handleDateChange}
                slotProps={{ textField: { variant: "outlined", size: "small" } }}
              />
            </LocalizationProvider>
            <IconButton onClick={handleNext}>
              <NextIcon />
            </IconButton>
            <Button 
              variant="outlined" 
              startIcon={<TodayIcon />} 
              onClick={handleToday}
              size="small"
            >
              Heute
            </Button>
          </Box>
          
          <Typography variant="h6">
            {currentDate.toLocaleDateString('de-DE', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showFullDay}
                  onChange={(e) => setShowFullDay(e.target.checked)}
                  disabled={isAutoFullDay}
                />
              }
              label={isAutoFullDay ? "Ganzer Tag (automatisch)" : "Ganzer Tag"}
            />
            {!showFullDay && !isAutoFullDay && (
              <ToggleButtonGroup
                value={zoomLevel}
                exclusive
                onChange={(e, newZoom) => newZoom && setZoomLevel(newZoom)}
                size="small"
              >
                <ToggleButton value="12h" aria-label="12 Stunden">
                  <Tooltip title="12 Stunden Ansicht">
                    <span>12h</span>
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="6h" aria-label="6 Stunden">
                  <Tooltip title="6 Stunden Ansicht">
                    <span>6h</span>
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="3h" aria-label="3 Stunden">
                  <Tooltip title="3 Stunden Ansicht">
                    <span>3h</span>
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            )}
            <ViewToggleGroup>
              <Button 
                variant={currentView === 'gantt' ? 'contained' : 'outlined'} 
                startIcon={<TimelineIcon />}
                onClick={() => setCurrentView('gantt')}
                size="small"
              >
                Gantt
              </Button>
              <Button 
                variant={currentView === 'week' ? 'contained' : 'outlined'} 
                startIcon={<WeekViewIcon />}
                onClick={() => setCurrentView('week')}
                size="small"
              >
                Woche
              </Button>
            </ViewToggleGroup>
          </Box>
        </GanttHeader>
        
        <TimelineContainer>
          <Box sx={{ display: 'flex', flexShrink: 0, borderBottom: 1, borderColor: 'divider' }}>
            <Box
              sx={{
                width: '250px',
                minWidth: '250px',
                height: '50px',
                p: 2,
                borderRight: 1,
                borderColor: 'divider',
                bgcolor: 'background.default',
                zIndex: 2
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold">
                Fahrer
              </Typography>
            </Box>
            <Box ref={timelineHeaderRef} sx={{ flex: 1, overflow: 'hidden' }}>
              <TimeHeader style={{ minWidth: `${timeSlots.length * getSlotWidth()}px` }}>
                {timeSlots.map((slot, index) => (
                  <TimeSlot key={`${slot.date.getTime()}-${slot.displayHour}`} isCurrentTime={slot.isCurrentTime} slotWidth={getSlotWidth()}>
                    <Typography variant="caption">
                      {slot.time}
                      {slot.isNextDay && (
                        <Typography variant="caption" sx={{ display: 'block', fontSize: '0.6rem', color: 'text.secondary' }}>
                          +1 Tag
                        </Typography>
                      )}
                      {slot.isPreviousDay && (
                        <Typography variant="caption" sx={{ display: 'block', fontSize: '0.6rem', color: 'text.secondary' }}>
                          -1 Tag
                        </Typography>
                      )}
                    </Typography>
                  </TimeSlot>
                ))}
              </TimeHeader>
            </Box>
          </Box>
          <Box
            onScroll={handleTimelineBodyScroll}
            sx={{ flex: 1, overflow: 'auto' }}
          >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '250px max-content',
              alignItems: 'stretch',
              minWidth: 'max-content'
            }}
          >
            {driverRows.map((row) => {
              const rowActivities = getActivitiesForCalendarRow(row);
              
              return (
                <React.Fragment key={row.id}>
                  <VehicleRow
                    sx={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 4,
                      bgcolor: 'background.default',
                      borderRight: 1,
                      borderColor: 'divider'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: 1, minWidth: 0 }}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                          <VehicleNumberBadge value={row.badgeLabel} compact />
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            noWrap
                            sx={{ minWidth: 0 }}
                          >
                            {row.primaryLabel}
                          </Typography>
                        </Box>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          noWrap
                          sx={{ display: 'block', minWidth: 0 }}
                        >
                          {row.secondaryLabel}
                        </Typography>
                      </Box>
                    </Box>
                  </VehicleRow>
                  <VehicleTimelineRow style={{ minWidth: `${timeSlots.length * getSlotWidth()}px` }}>
                    {timeSlots.map((slot) => (
                      <TimeSlot key={`${slot.date.getTime()}-${slot.displayHour}`} slotWidth={getSlotWidth()} />
                    ))}

                    {/* Schichtbeginn-Markierungen */}
                    {getShiftStartMarkers(row).map((marker, index) => (
                      <Tooltip
                        key={`shift-start-${index}`}
                        title={
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              Schichtbeginn
                            </Typography>
                            <Typography variant="body2">
                              Fahrer: {marker.driverName}
                            </Typography>
                            <Typography variant="body2">
                              Zeit: {formatTime(marker.time)}
                            </Typography>
                            <Typography variant="body2">
                              Fahrzeug: {marker.vehicleModel}
                            </Typography>
                          </Box>
                        }
                        arrow
                        placement="top"
                      >
                        <ShiftStartMarker
                          style={{
                            left: `${marker.position}px`
                          }}
                        />
                      </Tooltip>
                    ))}

                    {/* Schichtende-Markierungen */}
                    {getShiftEndMarkers(row).map((marker, index) => (
                      <Tooltip
                        key={`shift-end-${index}`}
                        title={
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              Schichtende
                            </Typography>
                            <Typography variant="body2">
                              Fahrer: {marker.driverName}
                            </Typography>
                            <Typography variant="body2">
                              Zeit: {formatTime(marker.time)}
                            </Typography>
                            <Typography variant="body2">
                              Fahrzeug: {marker.vehicleModel}
                            </Typography>
                          </Box>
                        }
                        arrow
                        placement="top"
                      >
                        <ShiftEndMarker
                          style={{
                            left: `${marker.position}px`
                          }}
                        />
                      </Tooltip>
                    ))}

                    {rowActivities.map((activity) => {
                      const endTime = formatTime(activity.endTime);
                      const activityLabel = getActivityBarLabel(activity, endTime);
                      const activityDriver = driversById.get(activity.driverId);
                      const driverName = activity.driverName || row.primaryLabel || getDriverName(activityDriver);
                      const vehicle = row.vehicle || vehicleByDriverId.get(activity.driverId) || vehiclesRef.current?.find(v => v.current_driver?.id === activity.driverId);
                      const vehicleLabel = getVehicleTooltipLabel(vehicle, row.badgeLabel);
                      const rideTypeLabel = getRideTypeLabel(activity.orderType);
                      const statusLabel = getActivityStatusLabel(activity);
                      const isRide = activity.type === 'ride';
                      const isBreak = activity.type === 'break' || activity.type === 'pause';
                      const plannedEndDiffers = activity.endTimePlanned &&
                        activity.endTime &&
                        Math.abs(activity.endTime.getTime() - activity.endTimePlanned.getTime()) > 60000;
                      const plannedEnd = plannedEndDiffers ? formatTime(activity.endTimePlanned) : null;
                      // Abgeschlossene Fahrten: tatsächlich gefahrene Dauer
                      // (Abholung → Abschluss) statt der Schätzung anzeigen.
                      const actualRideMinutes = getActualRideDurationMinutes(activity);
                      const rideDurationIsActual = actualRideMinutes !== null;
                      const rideDuration = rideDurationIsActual
                        ? formatMinutesLabel(actualRideMinutes)
                        : (formatMinutesLabel(activity.estimatedTimeMinutes) ||
                          formatDurationBetween(activity.mainStartTime || activity.startTime, activity.endTime));
                      const breakDuration = formatMinutesLabel(activity.plannedDuration) ||
                        formatDurationBetween(activity.startTime, activity.endTime);
                      const vehicleRequirementLabel = isRide ? getRideVehicleRequirementLabel(activity) : null;
                      const activityTitle = isRide
                        ? `${rideTypeLabel}${activity.isDelayed ? ' · verspätet' : ''}`
                        : isBreak
                          ? 'Pause'
                          : activity.type === 'offline'
                            ? 'Offline-Periode'
                            : activity.type === 'reconnect'
                              ? 'Wiederverbindung'
                              : activity.type === 'planned_shift'
                                ? 'Geplante Schicht'
                                : 'Aktivität';

                      // Separate Tooltips für Anfahrtszeit und Hauptfahrt
                      const approachTooltipContent = (
                        <Box sx={{ minWidth: 230, maxWidth: 320, textAlign: 'left' }}>
                          <TooltipTitle>
                            Anfahrt zur {rideTypeLabel}
                          </TooltipTitle>
                          <TooltipLine label="Status" value={statusLabel} />
                          <TooltipLine label="Kunde" value={activity.customerName} />
                          <TooltipLine label="Startadresse" value={activity.startAddress || 'Start offen'} />
                          <TooltipLine label="Ziel" value={activity.destination || 'Ziel offen'} />
                          <TooltipLine label="Anforderung" value={vehicleRequirementLabel} />
                          <TooltipLine
                            label="Anfahrt"
                            value={`${formatTime(activity.approachStartTime)} - ${formatTime(activity.mainStartTime)}`}
                          />
                          <TooltipLine
                            label="Dauer"
                            value={formatMinutesLabel(activity.approachTimeMinutes)}
                          />
                          <TooltipLine label="Abholung" value={formatTime(activity.mainStartTime)} />
                          <TooltipLine label="Notizen" value={activity.notes} />
                          <TooltipNote>
                            Fahrer ist für diese Vorbestellung bereits auf dem Weg zum Abholort.
                          </TooltipNote>
                        </Box>
                      );

                    const mainTooltipContent = (
                      <Box sx={{ minWidth: 230, maxWidth: 320, textAlign: 'left' }}>
                        <TooltipTitle color={activity.isDelayed ? 'error.main' : 'text.primary'}>
                          {activityTitle}
                        </TooltipTitle>
                        <TooltipLine label="Status" value={statusLabel} />
                        <TooltipLine label="Kunde" value={activity.customerName} />
                        <TooltipLine label="Startadresse" value={activity.startAddress || 'Start offen'} />
                        <TooltipLine label="Ziel" value={activity.destination || 'Ziel offen'} />
                        <TooltipLine label="Anforderung" value={vehicleRequirementLabel} />
                        <TooltipLine label="Abholung" value={formatTime(activity.mainStartTime)} />
                        <TooltipLine label="Ende" value={endTime} />
                        <TooltipLine label="Geplantes Ende" value={plannedEnd} />
                        <TooltipLine label={rideDurationIsActual ? 'Fahrtdauer (gefahren)' : 'Fahrtdauer (geschätzt)'} value={rideDuration} />
                        <TooltipLine
                          label="Anfahrt"
                          value={activity.approachTimeMinutes ? formatMinutesLabel(activity.approachTimeMinutes) : null}
                        />
                        <TooltipLine label="Notizen" value={activity.notes} />
                      </Box>
                    );

                    // Enhanced tooltip content with offline activity support
                    const normalTooltipContent = (
                      <Box sx={{ minWidth: 230, maxWidth: 320, textAlign: 'left' }}>
                        <TooltipTitle color={activity.isDelayed ? 'error.main' : 'text.primary'}>
                          {activityTitle}
                        </TooltipTitle>
                        
                        {/* Offline activity specific content */}
                        {activity.type === 'offline' && (
                          <>
                            <TooltipLine label="Status" value={activity.endTime && activity.endTime <= new Date() ? statusLabel : 'Noch offline'} />
                            <TooltipLine label="Grund" value={getOfflineReasonText(activity.reason || activity.offlineReason)} />
                            <TooltipLine label="Fahrer" value={driverName} />
                            <TooltipLine label="Fahrzeug" value={vehicleLabel} />
                            <TooltipLine label="Start" value={formatTime(activity.startTime)} />
                            <TooltipLine label="Ende" value={activity.endTime && activity.endTime <= new Date() ? endTime : null} />
                            <TooltipLine label="Dauer" value={calculateOfflineDuration(activity.startTime, activity.endTime)} />
                            <TooltipNote>
                              Offline-Zeit ist eine interne Blockadezeit und wird nicht als Pause gewertet.
                            </TooltipNote>
                          </>
                        )}
                        
                        {/* Reconnect activity specific content */}
                        {activity.type === 'reconnect' && (
                          <>
                            <TooltipLine label="Status" value="Wieder online" />
                            <TooltipLine label="Fahrer" value={driverName} />
                            <TooltipLine label="Fahrzeug" value={vehicleLabel} />
                            <TooltipLine label="Zeit" value={formatTime(activity.startTime)} />
                          </>
                        )}
                        
                        {/* Planned shift specific content */}
                        {activity.type === 'planned_shift' && (
                          <>
                            <TooltipLine label="Fahrer" value={activity.driverName || driverName} />
                            <TooltipLine label="Fahrzeug" value={vehicleLabel} />
                            <TooltipLine label="Schichtbeginn" value={formatTime(activity.startTime)} />
                            <TooltipLine label="Schichtende" value={formatTime(activity.endTime)} />
                            <TooltipLine label="Geplante Dauer" value={`${activity.plannedDuration || 8} Stunden`} />
                            <TooltipLine label="Notizen" value={activity.notes} />
                            <TooltipNote color="warning.main">
                              Geplante Schicht in der Flottenauslastung.
                            </TooltipNote>
                          </>
                        )}
                        
                        {/* Standard content for rides and breaks */}
                        {activity.type !== 'offline' && activity.type !== 'reconnect' && activity.type !== 'planned_shift' && (
                          <>
                            {isRide && (
                              <>
                                <TooltipLine label="Status" value={statusLabel} />
                                <TooltipLine label="Kunde" value={activity.customerName} />
                                <TooltipLine label="Startadresse" value={activity.startAddress || 'Start offen'} />
                                <TooltipLine label="Ziel" value={activity.destination || 'Ziel offen'} />
                                <TooltipLine label="Anforderung" value={vehicleRequirementLabel} />
                                <TooltipLine label="Abholung/Start" value={formatTime(activity.startTime)} />
                                <TooltipLine label="Ende" value={endTime} />
                                <TooltipLine label="Geplantes Ende" value={plannedEnd} />
                                <TooltipLine label={rideDurationIsActual ? 'Fahrtdauer (gefahren)' : 'Fahrtdauer (geschätzt)'} value={rideDuration} />
                                <TooltipLine label="Notizen" value={activity.notes} />
                              </>
                            )}
                            {isBreak && (
                              <>
                                <TooltipLine label="Typ" value={activity.destination || 'Pause'} />
                                <TooltipLine label="Fahrer" value={driverName} />
                                <TooltipLine label="Fahrzeug" value={vehicleLabel} />
                                <TooltipLine label="Start" value={formatTime(activity.startTime)} />
                                <TooltipLine label="Ende" value={endTime} />
                                <TooltipLine label="Dauer" value={breakDuration} />
                              </>
                            )}
                          </>
                        )}
                      </Box>
                    );
                    
                    if (activity.hasApproachTime) {
                      // Render approach time + main activity as separate bars
                      const approachPosition = calculateApproachTimePosition(activity);
                      const mainPosition = calculateMainActivityPosition(activity);
                      
                      return (
                        <React.Fragment key={activity.id}>
                          {/* Anfahrtszeit */}
                          <Tooltip 
                            title={approachTooltipContent}
                            arrow
                            placement="top"
                            enterDelay={200}
                            componentsProps={richTooltipComponentsProps}
                          >
                            <ApproachTimeBar
                              orderType={activity.orderType}
                              isDelayed={activity.isDelayed}
                              style={approachPosition}
                              onClick={(e) => handleActivityClick(e, activity)}
                            >
                              Anfahrt
                            </ApproachTimeBar>
                          </Tooltip>
                          
                          {/* Hauptfahrt */}
                          <Tooltip 
                            title={mainTooltipContent}
                            arrow
                            placement="top"
                            enterDelay={200}
                            componentsProps={richTooltipComponentsProps}
                          >
                            <ActivityBar
                              type={activity.type}
                              isDelayed={activity.isDelayed}
                              isPending={['scheduled', 'pre_accepted', 'accepted', 'arrived'].includes(activity.status)}
                              orderType={activity.orderType}
                              reason={activity.reason || activity.offlineReason}
                              style={{
                                ...mainPosition,
                                borderRadius: '0 4px 4px 0' // Nur rechts abgerundet
                              }}
                              onClick={(e) => handleActivityClick(e, activity)}
                            >
                              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {activityLabel}
                              </Box>
                              {activity.isDelayed && <WarningIcon sx={{ ml: 0.5, fontSize: '12px' }} />}
                            </ActivityBar>
                          </Tooltip>
                        </React.Fragment>
                      );
                    } else {
                      // Normale Aktivität ohne Anfahrtszeit
                      const position = calculateActivityPosition(activity);
                      
                      return (
                        <Tooltip 
                          key={activity.id}
                          title={normalTooltipContent}
                          arrow
                          placement="top"
                          enterDelay={200}
                          componentsProps={richTooltipComponentsProps}
                        >
                          <ActivityBar
                            type={activity.type}
                            isDelayed={activity.isDelayed}
                            isPending={['scheduled', 'pre_accepted', 'accepted', 'arrived'].includes(activity.status)}
                            orderType={activity.orderType}
                            reason={activity.reason || activity.offlineReason}
                            style={position}
                            onClick={(e) => handleActivityClick(e, activity)}
                          >
                            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {activityLabel}
                            </Box>
                            {activity.isDelayed && <WarningIcon sx={{ ml: 0.5, fontSize: '12px' }} />}
                          </ActivityBar>
                        </Tooltip>
                      );
                    }
                    })}
                    <CurrentTimeLine style={{ left: `${currentTimePos}px` }} />
                  </VehicleTimelineRow>
                </React.Fragment>
              );
            })}

          <FleetUtilizationRow sx={{ display: 'contents' }}>
            <UtilizationLabel
              sx={{
                position: 'sticky',
                left: 0,
                bottom: 50,
                zIndex: 4,
                bgcolor: 'background.default',
                borderTop: 1,
                borderColor: 'divider'
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="subtitle2">
                      Flottenauslastung
                    </Typography>
                    {(() => {
                      // Verwende globale Wartezeitdaten
                      const waitTimes = globalWaitTimes;
                      const nextUnavailable = globalNextUnavailable;
                      
                      // Intelligente Farbwahl basierend auf Wartezeit
                      const getWaitTimeColor = (waitTime) => {
                        if (waitTime.includes("nicht verfügbar") || 
                            waitTime.includes("Keine Fahrer verfügbar") || 
                            waitTime.includes("30+")) {
                          return "error.main"; // Rot für nicht verfügbar
                        } else if (waitTime.includes("nach Ausfallzeit") || waitTime.startsWith("2") || waitTime.startsWith("3")) {
                          return "warning.main"; // Orange für längere Wartezeiten
                        } else {
                          return "success.main"; // Grün für kurze Wartezeiten
                        }
                      };
                      
                      return (
                        <Box>
                          <Typography variant="caption" color={getWaitTimeColor(waitTimes.general)} sx={{ fontWeight: 'bold', display: 'block' }}>
                            Aktuelle Wartezeit: {waitTimes.general}
                          </Typography>
                          {nextUnavailable && (
                            <Typography variant="caption" color="error.main" sx={{ fontSize: '0.65rem', display: 'block', mt: 0.5 }}>
                              Keine Fahrer verfügbar in {nextUnavailable.minutesUntil} Min (für {nextUnavailable.duration} Min)
                            </Typography>
                          )}
                        </Box>
                      );
                    })()}
                  </Box>
                  {existingVehicleTypes.has(2) && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Großraum (6 Sitze)
                    </Typography>
                    {(() => {
                      const waitTimes = globalWaitTimes;
                      const isUnavailable = waitTimes.grossraum === "nicht verfügbar" || waitTimes.grossraum === "Keine Fahrer verfügbar";
                      return (
                        <Typography variant="caption" color={isUnavailable ? "error.main" : "success.main"} sx={{ fontSize: '0.65rem', display: 'block' }}>
                          Wartezeit: {waitTimes.grossraum}
                        </Typography>
                      );
                    })()}
                  </Box>
                  )}
                  {existingVehicleTypes.has(3) && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Kleinbus (7 Sitze)
                    </Typography>
                    {(() => {
                      const waitTimes = globalWaitTimes;
                      const isUnavailable = waitTimes.kleinbus7 === "nicht verfügbar" || waitTimes.kleinbus7 === "Keine Fahrer verfügbar";
                      return (
                        <Typography variant="caption" color={isUnavailable ? "error.main" : "success.main"} sx={{ fontSize: '0.65rem', display: 'block' }}>
                          Wartezeit: {waitTimes.kleinbus7}
                        </Typography>
                      );
                    })()}
                  </Box>
                  )}
                  {existingVehicleTypes.has(4) && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Kleinbus (8 Sitze)
                    </Typography>
                    {(() => {
                      const waitTimes = globalWaitTimes;
                      const isUnavailable = waitTimes.kleinbus8 === "nicht verfügbar" || waitTimes.kleinbus8 === "Keine Fahrer verfügbar";
                      return (
                        <Typography variant="caption" color={isUnavailable ? "error.main" : "success.main"} sx={{ fontSize: '0.65rem', display: 'block' }}>
                          Wartezeit: {waitTimes.kleinbus8}
                        </Typography>
                      );
                    })()}
                  </Box>
                  )}
                </Box>
              </Box>
            </UtilizationLabel>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                minWidth: `${memoizedTimeSlots.length * getSlotWidth()}px`,
                position: 'sticky',
                bottom: 50,
                zIndex: 3,
                bgcolor: 'background.default',
                borderTop: 1,
                borderColor: 'divider'
              }}
            >
              <UtilizationChart>
                <UtilizationTimeline style={{ width: `${memoizedTimeSlots.length * getSlotWidth()}px` }}>
                  {(() => {
                    const { segments } = availabilityTimeline;
                    
                    return segments.map((segment, index) => {
                      // Berechne Position in Pixeln basierend auf der gleichen Logik wie die Zeitslots
                      const slotWidth = getSlotWidth();
                      const leftPx = (segment.startMinute / 60) * slotWidth;
                      const widthPx = ((segment.endMinute - segment.startMinute) / 60) * slotWidth;
                      
                      const formatTimeRange = () => {
                        const startTime = segment.startTime.toLocaleTimeString('de-DE', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        });
                        const endTime = segment.endTime.toLocaleTimeString('de-DE', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        });
                        return `${startTime} - ${endTime}`;
                      };
                      
                      return (
                        <Tooltip
                          key={index}
                          title={
                            <Box>
                              <Typography variant="caption" fontWeight="bold">
                                {formatTimeRange()}
                              </Typography>
                              <Typography variant="caption" display="block">
                                Status: {
                                  segment.status === 'normal' ? 'Verfügbar (normale Schichtzeit)' :
                                  segment.status === 'overtime' ? 'Verfügbar (nur Überstunden)' :
                                  segment.status === 'planned' ? 'Geplante Schichten' :
                                  'Nicht verfügbar'
                                }
                              </Typography>
                              {segment.available && (
                                <>
                                  <Typography variant="caption" display="block">
                                    Verfügbare Fahrer: {segment.availableCount} von {segment.totalWithDriver}
                                  </Typography>
                                  {segment.availableWithinShiftHours > 0 && (
                                    <Typography variant="caption" display="block">
                                      - Innerhalb Schichtzeit: {segment.availableWithinShiftHours}
                                    </Typography>
                                  )}
                                  {segment.availableOverShiftHours > 0 && (
                                    <Typography variant="caption" display="block">
                                      - Überstunden: {segment.availableOverShiftHours}
                                    </Typography>
                                  )}
                                </>
                              )}
                              {segment.status === 'planned' && segment.plannedShiftsCount && (
                                <Typography variant="caption" display="block">
                                  Geplante Schichten: {segment.plannedShiftsCount}
                                </Typography>
                              )}
                              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                {segment.status === 'planned' ? 'Flottenauslastung geplant' : 
                                `Geschätzte Wartezeit: ${
                                  segment.status === 'normal' || segment.status === 'overtime' ? '5-10 Min' :
                                  '20+ Min'
                                }`}
                              </Typography>
                            </Box>
                          }
                          arrow
                          placement="top"
                        >
                          <UtilizationSegment
                            status={segment.status}
                            style={{
                              left: `${leftPx}px`,
                              width: `${widthPx}px`
                            }}
                          />
                        </Tooltip>
                      );
                    });
                  })()}
                </UtilizationTimeline>
              </UtilizationChart>
              
              <UtilizationChart>
                <UtilizationTimeline style={{ width: `${memoizedTimeSlots.length * getSlotWidth()}px` }}>
                  {(() => {
                    const { segments } = kombiAvailability; // Type 2 = Kombi
                    
                    return segments.map((segment, index) => {
                      const slotWidth = getSlotWidth();
                      const leftPx = (segment.startMinute / 60) * slotWidth;
                      const widthPx = ((segment.endMinute - segment.startMinute) / 60) * slotWidth;
                      
                      const formatTimeRange = () => {
                        const startTime = segment.startTime.toLocaleTimeString('de-DE', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        });
                        const endTime = segment.endTime.toLocaleTimeString('de-DE', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        });
                        return `${startTime} - ${endTime}`;
                      };
                      
                      return (
                        <Tooltip
                          key={index}
                          title={
                            <Box>
                              <Typography variant="caption" fontWeight="bold">
                                {formatTimeRange()}
                              </Typography>
                              <Typography variant="caption" display="block">
                                Status: {
                                  segment.status === 'normal' ? 'Verfügbar (normale Schichtzeit)' :
                                  segment.status === 'overtime' ? 'Verfügbar (nur Überstunden)' :
                                  segment.status === 'planned' ? 'Geplante Schichten' :
                                  'Nicht verfügbar'
                                }
                              </Typography>
                              {segment.available && (
                                <>
                                  <Typography variant="caption" display="block">
                                    Verfügbar für 6+ Personen: {segment.availableCount}
                                  </Typography>
                                  {segment.availableType2Count > 0 && (
                                    <Typography variant="caption" display="block">
                                      - Kombis (6 Sitze): {segment.availableType2Count}
                                    </Typography>
                                  )}
                                  {segment.availableType3Count > 0 && (
                                    <Typography variant="caption" display="block">
                                      - Minibusse (8 Sitze): {segment.availableType3Count}
                                    </Typography>
                                  )}
                                  <Typography variant="caption" display="block">
                                    Gesamt Fahrzeuge: {segment.totalVehicles}
                                  </Typography>
                                </>
                              )}
                              {segment.status === 'planned' && segment.plannedShiftsCount && (
                                <Typography variant="caption" display="block">
                                  Geplante Schichten: {segment.plannedShiftsCount}
                                </Typography>
                              )}
                              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                {segment.status === 'planned' ? 'Flottenauslastung geplant' :
                                 segment.totalVehicles === 0 ? 'Keine passenden Fahrzeuge vorhanden' : 
                                 segment.available ? 'Fahrzeug für 6+ Personen verfügbar' : 'Kein Fahrzeug für 6+ Personen verfügbar'}
                              </Typography>
                            </Box>
                          }
                          arrow
                          placement="top"
                        >
                          <UtilizationSegment
                            status={segment.status}
                            style={{
                              left: `${leftPx}px`,
                              width: `${widthPx}px`
                            }}
                          />
                        </Tooltip>
                      );
                    });
                  })()}
                </UtilizationTimeline>
              </UtilizationChart>
              
              <UtilizationChart>
                <UtilizationTimeline style={{ width: `${memoizedTimeSlots.length * getSlotWidth()}px` }}>
                  {(() => {
                    const { segments } = minibusAvailability; // Type 3 = Kleinbus
                    
                    return segments.map((segment, index) => {
                      const slotWidth = getSlotWidth();
                      const leftPx = (segment.startMinute / 60) * slotWidth;
                      const widthPx = ((segment.endMinute - segment.startMinute) / 60) * slotWidth;
                      
                      const formatTimeRange = () => {
                        const startTime = segment.startTime.toLocaleTimeString('de-DE', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        });
                        const endTime = segment.endTime.toLocaleTimeString('de-DE', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        });
                        return `${startTime} - ${endTime}`;
                      };
                      
                      return (
                        <Tooltip
                          key={index}
                          title={
                            <Box>
                              <Typography variant="caption" fontWeight="bold">
                                {formatTimeRange()}
                              </Typography>
                              <Typography variant="caption" display="block">
                                Status: {
                                  segment.status === 'normal' ? 'Verfügbar (normale Schichtzeit)' :
                                  segment.status === 'overtime' ? 'Verfügbar (nur Überstunden)' :
                                  segment.status === 'planned' ? 'Geplante Schichten' :
                                  'Nicht verfügbar'
                                }
                              </Typography>
                              {segment.available && (
                                <>
                                  <Typography variant="caption" display="block">
                                    Verfügbare Minibusse: {segment.availableCount} von {segment.totalWithDriver}
                                  </Typography>
                                  <Typography variant="caption" display="block">
                                    Gesamt Minibusse: {segment.totalVehicles}
                                  </Typography>
                                </>
                              )}
                              {segment.status === 'planned' && segment.plannedShiftsCount && (
                                <Typography variant="caption" display="block">
                                  Geplante Schichten: {segment.plannedShiftsCount}
                                </Typography>
                              )}
                              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                {segment.status === 'planned' ? 'Flottenauslastung geplant' :
                                 segment.totalVehicles === 0 ? 'Keine Minibusse vorhanden' : 
                                 segment.available ? 'Minibus verfügbar' : 'Kein Minibus verfügbar'}
                              </Typography>
                            </Box>
                          }
                          arrow
                          placement="top"
                        >
                          <UtilizationSegment
                            status={segment.status}
                            style={{
                              left: `${leftPx}px`,
                              width: `${widthPx}px`
                            }}
                          />
                        </Tooltip>
                      );
                    });
                  })()}
                </UtilizationTimeline>
              </UtilizationChart>
            </Box>
          </FleetUtilizationRow>
          
          <FleetUtilizationRow sx={{ display: 'contents' }}>
            <UtilizationLabel
              sx={{
                display: 'flex',
                alignItems: 'center',
                minHeight: '50px',
                height: '50px',
                position: 'sticky',
                left: 0,
                bottom: 0,
                zIndex: 4,
                bgcolor: 'background.default',
                borderTop: 1,
                borderColor: 'divider'
              }}
            >
              <Typography variant="subtitle2">
                Offene Vorbestellungen
              </Typography>
            </UtilizationLabel>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                position: 'sticky',
                bottom: 0,
                zIndex: 3,
                height: '50px',
                minWidth: `${memoizedTimeSlots.length * getSlotWidth()}px`,
                bgcolor: 'background.default',
                borderTop: 1,
                borderColor: 'divider'
              }}
            >
              <UtilizationTimeline style={{ 
                width: `${memoizedTimeSlots.length * getSlotWidth()}px`, 
                height: '20px',
                position: 'absolute',
                top: '50%',
                transform: 'translateY(-50%)'
              }}>
                {calculateScheduledRidesTimeline().map((ride, index) => {
                  const formatTimeRange = () => {
                    const startTime = ride.startTime.toLocaleTimeString('de-DE', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    });
                    const endTime = ride.endTime.toLocaleTimeString('de-DE', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    });
                    return `${startTime} - ${endTime}`;
                  };
                  
	                  return (
	                    <Tooltip
	                      key={ride.id}
	                      title={
	                        <Box sx={{ minWidth: 230, maxWidth: 320, textAlign: 'left' }}>
	                          <TooltipTitle>
	                            {formatTimeRange()}
	                          </TooltipTitle>
	                          <TooltipLine label="Status" value={ride.status === 'scheduled' ? 'Vorgebucht' : 'Offen'} />
	                          <TooltipLine label="Kunde" value={ride.customerName} />
	                          <TooltipLine label="Startadresse" value={ride.startAddress || 'Start offen'} />
	                          <TooltipLine label="Ziel" value={ride.destination || 'Ziel offen'} />
	                          <TooltipLine label="Anforderung" value={getRideVehicleRequirementLabel(ride)} />
	                          <TooltipLine label="Notizen" value={ride.notes} />
	                          {ride.overlaps && (
	                            <TooltipNote color="warning.main">
	                              Überlappung mit anderen Fahrten
	                            </TooltipNote>
	                          )}
	                        </Box>
	                      }
	                      arrow
	                      placement="top"
	                      componentsProps={richTooltipComponentsProps}
	                    >
                      <ScheduledRideBar
                        color={ride.color}
                        style={{
                          left: `${ride.left}px`,
                          width: `${ride.width}px`
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </UtilizationTimeline>
            </Box>
          </FleetUtilizationRow>
          </Box>
          </Box>
        </TimelineContainer>
      </GanttContainer>
    );
  };

  // Render Week Overview
  const renderWeekView = () => {
    const weekData = getWeekOverviewData();
    const weekDays = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
    
    return (
      <WeekOverviewContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Wochenübersicht</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={handlePrevious}>
              <PreviousIcon />
            </IconButton>
            <Typography variant="subtitle1">
              {currentDate.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
            </Typography>
            <IconButton onClick={handleNext}>
              <NextIcon />
            </IconButton>
          </Box>
        </Box>
        
        <WeekGrid>
          <WeekCell isHeader>Fahrzeug</WeekCell>
          {weekDays.map((day) => (
            <WeekCell key={day} isHeader>{day}</WeekCell>
          ))}
          
          {weekData.map(({ vehicle, week }) => (
            <React.Fragment key={vehicle.id}>
              <WeekCell>
                <Typography variant="body2" fontWeight="medium">
                  {vehicle.model || vehicle.name}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {vehicle.licensePlate}
                </Typography>
              </WeekCell>
              {week.map(({ day, utilization, hours }, index) => (
                <WeekCell 
                  key={index}
                  utilization={utilization}
                  onClick={() => {
                    setCurrentDate(day);
                    setCurrentView('gantt');
                  }}
                >
                  <Typography variant="body2" fontWeight="medium">
                    {hours}h
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {Math.round(utilization * 100)}%
                  </Typography>
                </WeekCell>
              ))}
            </React.Fragment>
          ))}
        </WeekGrid>
      </WeekOverviewContainer>
    );
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title={`${wording.vehicle}-Kalender`}
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Schichtplanung', path: '/schichten' }
        ]}
        secondaryButton={{
          label: 'Schichtplaner',
          icon: <ListIcon />,
          path: '/schichtplaner'
        }}
        actionButton={{
          label: 'Neue Fahrt',
          icon: <AddIcon />,
          onClick: () => openRideDialog?.({ mode: 'create' })
        }}
      />
      
      {currentView === 'gantt' ? renderGanttView() : renderWeekView()}
      
      {/* Activity Detail Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
      >
        {selectedActivity && (
          <>
            <MenuItem onClick={handleCloseMenu}>
              <ViewIcon sx={{ mr: 1 }} />
              Details anzeigen
            </MenuItem>
            {selectedActivity.type === 'ride' && (
              <MenuItem onClick={handleCloseMenu}>
                <EditIcon sx={{ mr: 1 }} />
                Fahrt bearbeiten
              </MenuItem>
            )}
          </>
        )}
      </Menu>
    </Box>
  );
};

export default ShiftCalendar;
